
import { useState, useEffect, useRef, Component } from "react";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

/* ═══════════════════════════════════════════════════════════
   NEXUS AI PLATFORM v5
   Real PPTX · Voice Input · File Upload (PDF/DOCX/IMG/TXT)
   Unsplash Images · Functional Websites · Template Previews
═══════════════════════════════════════════════════════════ */

/* ── Error Boundary ──────────────────────────────────────── */
class ErrorBoundary extends Component {
  constructor(props){super(props);this.state={hasError:false,error:null};}
  static getDerivedStateFromError(e){return{hasError:true,error:e};}
  componentDidCatch(e,info){console.error("[NEXUS] Module error:",e,info);}
  render(){
    if(this.state.hasError) return(
      <div style={{padding:32,textAlign:"center",fontFamily:"'Syne',sans-serif"}}>
        <div style={{fontSize:"2em",marginBottom:12}}>⚠</div>
        <div style={{fontWeight:700,color:"var(--txt)",marginBottom:8}}>Something went wrong</div>
        <div style={{fontSize:".8em",color:"var(--txt2)",marginBottom:20,maxWidth:400,margin:"0 auto 20px"}}>{this.state.error?.message||"An unexpected error occurred."}</div>
        <button onClick={()=>this.setState({hasError:false,error:null})}
          style={{background:"linear-gradient(135deg,#ff3c5f,#9b1dff)",border:"none",color:"#fff",padding:"10px 24px",borderRadius:9,fontFamily:"'Syne',sans-serif",fontWeight:700,cursor:"pointer"}}>
          Try Again
        </button>
      </div>
    );
    return this.props.children;
  }
}

let _apiKey = process.env.REACT_APP_GEMINI_KEY || "";
export function setApiKey(k) { _apiKey = k; }
export function getApiKey()  { return _apiKey; }

// ── Plan-aware rate limiter (server-side, MySQL) ──────────
let _sessionToken = "";
export function setSessionToken(t) { _sessionToken = t; }

// Plan limits (mirrors server PLAN_LIMITS)
const PLAN_LIMITS = {
  free:  { chat:10,    code:5,   web:1,  pdf:2,   ppt:2,   refine:3,   quiz:10,    tutor:10,    interview:1,  resume:1,  data:2  },
  paid:  { chat:10000, code:500, web:50, pdf:100, ppt:100, refine:200, quiz:99999, tutor:99999, interview:20, resume:10, data:60 },
  owner: { chat:999999,code:999999,web:999999,pdf:999999,ppt:999999,refine:999999,quiz:999999,tutor:999999,interview:999999,resume:999999,data:999999 },
};

// Current usage/plan state (loaded on mount, updated after each action)
let _usageState = { usage:{}, plan:"free", limits:PLAN_LIMITS.free };

export function setUsageState(s) { _usageState = s; }
export function getUsageState()  { return _usageState; }

const OWNER_EMAIL_CLIENT = "rajarshipaul20@gmail.com";

function isOwnerSession() {
  try {
    const s = JSON.parse(localStorage.getItem("nx_session_v2") || "null");
    return s && s.email === OWNER_EMAIL_CLIENT;
  } catch { return false; }
}

async function checkRate(action) {
  if (!action) return;
  // HARDCODED OWNER BYPASS — email checked directly from localStorage session
  if (isOwnerSession()) return;
  if (_usageState.plan === "owner") return;
  // Server-side check
  if (_sessionToken) {
    try {
      const res = await fetch("/api/usage/use", {
        method: "POST",
        headers: { "Content-Type":"application/json", "x-session-token": _sessionToken },
        body: JSON.stringify({ action }),
      });
      if (res.status === 429) {
        const d = await res.json();
        throw new Error(d.error || `Limit reached for "${action}"`);
      }
      if (res.ok) {
        const d = await res.json();
        _usageState.usage[action] = d.used;
        return;
      }
    } catch(e) {
      if (e.message && (e.message.includes("limit") || e.message.includes("Limit"))) throw e;
    }
  }
  // Fallback: localStorage daily limit if no session/DB
  const todayKey = new Date().toISOString().slice(0,10);
  const key   = "nx_rl_"+todayKey+"_"+action;
  const used  = parseInt(localStorage.getItem(key)||"0",10);
  const lim   = (_usageState.limits||PLAN_LIMITS.free)[action] || 10;
  if (used >= lim) throw new Error(`Daily limit reached for "${action}" (${lim}/day). Resets at midnight.`);
  localStorage.setItem(key, String(used+1));
}

// Legacy compat helpers (used by RateBadge + StudyModule stats)
const LIMITS = PLAN_LIMITS.free; // shown as fallback only
const today  = () => new Date().toISOString().slice(0,10);
const rKey   = a  => `nx_rl_${today()}_${a}`;
// Lines 100-104
const getUse = a => {
  // If we have a session and the usage key exists in our state, use it.
  if (_sessionToken && _usageState.usage && _usageState.usage[a] !== undefined) {
    return _usageState.usage[a];
  }
  // Otherwise fallback to localStorage (for guests)
  return parseInt(localStorage.getItem(rKey(a)) || "0", 10);
};
function remaining(action) {
  const lim = (_usageState.limits||PLAN_LIMITS.free)[action] || 20;
  return Math.max(0, lim - getUse(action));
}

// ── callAI ─────────────────────────────────────────────────
async function callAI(messages, system="", maxTokens=8192, action=null) {
  if(action) await checkRate(action);
  const body={ model:"gemini-2.0-flash", max_tokens:maxTokens,
    messages: messages.map(m=>({role:m.role, content:m.content})) };
  if(system) body.system=system;
  const res=await fetch("/api/chat",{method:"POST",headers:{"Content-Type":"application/json","x-api-key":_apiKey||""},body:JSON.stringify(body)});
  const data=await res.json();
  if(!res.ok) throw new Error(data?.error||`HTTP ${res.status}`);
  return data.content?.map(b=>b.text||"").join("")||"";
}

// ── callAI with multimodal content (images/docs) ──────────
async function callAIMulti(contentArray, system="", maxTokens=4096, action=null) {
  if(action) await checkRate(action);
  const body={ model:"gemini-2.0-flash", max_tokens:maxTokens,
    messages:[{ role:"user", content: contentArray }] };
  if(system) body.system=system;
  const res=await fetch("/api/chat",{method:"POST",headers:{"Content-Type":"application/json","x-api-key":_apiKey||""},body:JSON.stringify(body)});
  const data=await res.json();
  if(!res.ok) throw new Error(data?.error||`HTTP ${res.status}`);
  return data.content?.map(b=>b.text||"").join("")||"";
}

// ── Download real .pptx from server ───────────────────────
async function downloadPPTX(title, slides, theme="dark") {
  const res=await fetch("/api/pptx",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({title,slides,theme})});
  if(!res.ok){const e=await res.json().catch(()=>({})); throw new Error(e.error||"PPTX generation failed");}
  const blob=await res.blob();
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a"); a.href=url; a.download=`${title.replace(/\s+/g,"-").toLowerCase()}.pptx`; a.click();
  setTimeout(()=>URL.revokeObjectURL(url),2000);
}

// ── Extract clean HTML from Gemini ────────────────────────
function extractHTML(raw) {
  if(!raw) return "";
  let s=raw.trim()
    .replace(/^```html\s*/im,"").replace(/^```\s*/m,"")
    .replace(/\s*```\s*$/m,"").replace(/```html/gi,"").replace(/```/g,"").trim();
  const di=s.indexOf("<!DOCTYPE"), hi=s.indexOf("<html");
  const start=di!==-1?di:hi!==-1?hi:-1;
  if(start===-1) return "";
  const end=s.lastIndexOf("</html>");
  return end!==-1 ? s.slice(start,end+7) : s.slice(start);
}

// ── Check if HTML is complete (no truncation) ─────────────
function isHTMLComplete(html) {
  if(!html||html.length<500) return false;
  const h=html.toLowerCase();
  // Must have all closing tags in correct order
  const hasClosingBody = h.includes("</body>");
  const hasClosingHtml = h.includes("</html>");
  // Check for unclosed script or style tags (truncation signature)
  const scriptOpens  = (h.match(/<script/g)||[]).length;
  const scriptCloses = (h.match(/<\/script>/g)||[]).length;
  const styleOpens   = (h.match(/<style/g)||[]).length;
  const styleCloses  = (h.match(/<\/style>/g)||[]).length;
  const scriptsOk = scriptCloses >= scriptOpens;
  const stylesOk  = styleCloses  >= styleOpens;
  return hasClosingBody && hasClosingHtml && scriptsOk && stylesOk;
}

// ── Complete truncated HTML with a second Gemini call ─────
async function completeHTML(partial) {
  // Find the last complete tag to use as a seam
  const lastTag = partial.lastIndexOf(">");
  const truncatedAt = partial.slice(Math.max(0, lastTag-1500), lastTag+1);
  const completion = await callAI(
    [{role:"user", content:`This HTML file was cut off mid-generation. Here is the END of what was generated so far:\n\n...${truncatedAt}\n\nContinue and COMPLETE the HTML from exactly where it cut off. Output ONLY the missing remainder — do not repeat what was already written. Close all open tags, finish any open <script> or <style> blocks, and end with </body></html>. No markdown, no backticks.`}],
    "You complete truncated HTML files. Output only the missing remainder to close the file properly. No markdown.",
    16384
  );
  const cleaned = completion.replace(/^```html\s*/i,"").replace(/^```/,"").replace(/```$/,"").trim();
  return partial + "\n" + cleaned;
}

// ── PDF Export ─────────────────────────────────────────────
function exportPDF(title, content) {
  const body=content
    .replace(/^# (.+)/gm,"<h1>$1</h1>").replace(/^## (.+)/gm,"<h2>$1</h2>")
    .replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>").replace(/\*(.+?)\*/g,"<em>$1</em>")
    .replace(/`([^`]+)`/g,"<code>$1</code>").replace(/^- (.+)/gm,"<li>$1</li>")
    .replace(/(<li>.*<\/li>\n?)+/g,m=>`<ul>${m}</ul>`)
    .split("\n\n").map(p=>`<p>${p.replace(/\n/g,"<br>")}</p>`).join("");
  const w=window.open("","_blank");
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>
<style>@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Source+Serif+4:wght@300;400;600&display=swap');
*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Source Serif 4',Georgia,serif;font-size:11.5pt;line-height:1.9;color:#111;padding:72px 80px;max-width:820px;margin:0 auto}
.cover{border-bottom:2px solid #111;padding-bottom:52px;margin-bottom:52px}.badge{font-size:8pt;letter-spacing:4px;text-transform:uppercase;color:#aaa;margin-bottom:18px}
.main-title{font-family:'Playfair Display',serif;font-size:32pt;font-weight:700;line-height:1.1;color:#0a0a0a;margin-bottom:18px}.rule{width:50px;height:2px;background:#111;margin:22px 0}
h1{font-family:'Playfair Display',serif;font-size:19pt;margin:40px 0 12px}h2{font-family:'Playfair Display',serif;font-size:14pt;font-weight:700;margin:28px 0 10px}
p{margin-bottom:13px}ul{margin:0 0 13px 24px}li{margin-bottom:4px}code{background:#f0f0f0;padding:1px 5px;font-family:monospace}
.footer{margin-top:56px;padding-top:14px;border-top:1px solid #ddd;font-size:8pt;color:#bbb;display:flex;justify-content:space-between}
</style></head><body>
<div class="cover"><div class="badge">NEXUS AI · Professional Document</div><div class="main-title">${title}</div><div class="rule"/>
<div style="font-size:9pt;color:#777">${new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"})}</div></div>
${body}<div class="footer"><span>NEXUS Intelligence</span><span>${new Date().getFullYear()}</span></div>
<script>window.onload=()=>setTimeout(()=>window.print(),500)<\/script></body></html>`);
  w.document.close();
}

// ── Markdown renderer ──────────────────────────────────────
function md(t="") {
  return t
    .replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>").replace(/\*(.+?)\*/g,"<em>$1</em>")
    .replace(/`([^`]+)`/g,`<code style="background:rgba(255,60,95,.15);padding:2px 6px;border-radius:4px;font-size:.88em;font-family:monospace">$1</code>`)
    .replace(/^## (.+)/gm,`<div style="font-weight:700;color:var(--R);margin:10px 0 4px;font-size:.92em">$1</div>`)
    .replace(/^- (.+)/gm,`<div style="padding-left:14px;margin:2px 0">• $1</div>`)
    .replace(/\n/g,"<br>");
}

// ── Rate badge ─────────────────────────────────────────────
function RateBadge({action}) {
  const left=remaining(action), lim=(_usageState.limits||PLAN_LIMITS.free)[action]||20, pct=lim>0?left/lim:1;
  const col=pct>0.5?"#a8ff78":pct>0.2?"#ffd166":"#ff3c5f";
  return <span style={{fontSize:".66em",background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.09)",borderRadius:20,padding:"2px 9px",color:col,fontFamily:"monospace",whiteSpace:"nowrap"}}>{left}/{lim} left</span>;
}

// ── File to base64 ─────────────────────────────────────────
function fileToBase64(file) {
  return new Promise((res,rej)=>{
    const r=new FileReader();
    r.onload=()=>res(r.result.split(",")[1]);
    r.onerror=rej;
    r.readAsDataURL(file);
  });
}

// ── Resume templates ───────────────────────────────────────
const RESUME_TEMPLATES = [
  {id:"t1",  name:"Executive Edge",    accent:"1a1a2e", bg:"ffffff", style:"classic"},
  {id:"t2",  name:"Tech Minimal",      accent:"0f4c75", bg:"f8fbff", style:"modern"},
  {id:"t3",  name:"Creative Bold",     accent:"c0392b", bg:"fffefe", style:"bold"},
  {id:"t4",  name:"Finance Pro",       accent:"2c3e50", bg:"fafafa", style:"classic"},
  {id:"t5",  name:"Modern Split",      accent:"6c3483", bg:"fdf8ff", style:"modern"},
  {id:"t6",  name:"Clean Serif",       accent:"1e8449", bg:"f8fff8", style:"classic"},
  {id:"t7",  name:"Silicon Valley",    accent:"0078d4", bg:"f0f8ff", style:"modern"},
  {id:"t8",  name:"Harvard Style",     accent:"a93226", bg:"fffaf9", style:"classic"},
  {id:"t9",  name:"Consulting",        accent:"17202a", bg:"fafafa", style:"classic"},
  {id:"t10", name:"Design Lead",       accent:"e67e22", bg:"fffdf8", style:"bold"},
  {id:"t11", name:"Data Science",      accent:"1abc9c", bg:"f0fffc", style:"modern"},
  {id:"t12", name:"Medical Pro",       accent:"2e86ab", bg:"f8fbff", style:"classic"},
  {id:"t13", name:"Legal Brief",       accent:"212121", bg:"ffffff", style:"minimal"},
  {id:"t14", name:"Startup Founder",   accent:"8e44ad", bg:"fdf8ff", style:"bold"},
  {id:"t15", name:"Academic CV",       accent:"2c3e50", bg:"fafafa", style:"classic"},
  {id:"t16", name:"Global Manager",    accent:"16a085", bg:"f0fffc", style:"modern"},
  {id:"t17", name:"Product Manager",   accent:"d35400", bg:"fffaf5", style:"modern"},
  {id:"t18", name:"Marketing Pro",     accent:"c0392b", bg:"fff8f8", style:"bold"},
  {id:"t19", name:"Cloud Architect",   accent:"2980b9", bg:"f5faff", style:"modern"},
  {id:"t20", name:"Minimalist Ink",    accent:"000000", bg:"ffffff", style:"minimal"},
  {id:"t21", name:"Creative Director", accent:"c8a000", bg:"fffdf0", style:"bold"},
  {id:"t22", name:"UX Lead",           accent:"9b59b6", bg:"fdf8ff", style:"modern"},
];

function buildResumeHTML(tpl, d) {
  const a="#"+tpl.accent;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-size:10.8pt;line-height:1.75;color:#111;padding:36px 50px;max-width:800px;margin:0 auto;background:${tpl.bg}}
.name{font-size:22pt;font-weight:700;color:${a};margin-bottom:3px}
.contact{font-size:9pt;color:#555;margin-bottom:16px;padding-bottom:10px;border-bottom:1.5px solid ${a}}
h2{font-size:9pt;font-weight:700;text-transform:uppercase;letter-spacing:2.5px;color:${a};border-bottom:1px solid ${a};margin:16px 0 6px;padding-bottom:3px}
p,li{font-size:10.5pt;color:#222;line-height:1.7;margin-bottom:4px}ul{margin:4px 0 0 18px}
.role{font-weight:600;color:#111;font-size:10.5pt;margin-top:8px}
</style></head><body>
<div class="name">${d.name||"Your Name"}</div>
<div class="contact">${d.contact||"email · linkedin · phone · city"}</div>
${d.summary?`<h2>Summary</h2><p>${d.summary}</p>`:""}
${d.experience?`<h2>Experience</h2>${d.experience.split("\n").filter(Boolean).map(l=>l.startsWith("-")?`<li style="margin-left:18px">${l.slice(1).trim()}</li>`:`<div class="role">${l}</div>`).join("")}`:""}
${d.education?`<h2>Education</h2><p>${d.education}</p>`:""}
${d.skills?`<h2>Skills</h2><p>${d.skills}</p>`:""}
${d.extra?`<h2>Certifications & Awards</h2><p>${d.extra}</p>`:""}
</body></html>`;
}

// ── Template Preview Card ──────────────────────────────────
function TemplatePreview({tpl, selected, onClick}) {
  const a="#"+tpl.accent;
  const styleLabels={classic:"serif",modern:"sans",bold:"bold",minimal:"ink"};
  return (
    <div onClick={onClick} style={{cursor:"pointer",borderRadius:12,overflow:"hidden",border:`2px solid ${selected?"#ff3c5f":"rgba(255,255,255,.08)"}`,transition:"all .2s",transform:selected?"scale(1.03)":"scale(1)",background:"#fff",boxShadow:selected?"0 8px 30px rgba(255,60,95,.25)":"0 2px 8px rgba(0,0,0,.3)"}}>
      {/* Mini resume visual mockup */}
      <div style={{background:tpl.bg,padding:"10px 9px",height:128,overflow:"hidden",position:"relative"}}>
        {/* Accent bar top (for modern style) */}
        {tpl.style==="modern"&&<div style={{position:"absolute",top:0,left:0,right:0,height:4,background:a}}/>}
        {/* Left accent bar (for bold style) */}
        {tpl.style==="bold"&&<div style={{position:"absolute",top:0,left:0,width:4,bottom:0,background:a}}/>}
        <div style={{paddingLeft:tpl.style==="bold"?8:0,paddingTop:tpl.style==="modern"?6:0}}>
          {/* Name */}
          <div style={{width:"55%",height:9,background:a,borderRadius:2,marginBottom:4}}/>
          {/* Contact */}
          <div style={{width:"78%",height:5,background:"#ccc",borderRadius:1,marginBottom:6,opacity:.7}}/>
          {/* Divider */}
          <div style={{width:"100%",height:1,background:a,opacity:.3,marginBottom:5}}/>
          {/* Section label */}
          <div style={{width:"30%",height:4,background:a,borderRadius:1,marginBottom:4,opacity:.6}}/>
          {/* Lines */}
          {[90,75,85,65,80].map((w,i)=><div key={i} style={{width:`${w}%`,height:3,background:"#ddd",borderRadius:1,marginBottom:3}}/>)}
          {/* Section label 2 */}
          <div style={{width:"28%",height:4,background:a,borderRadius:1,marginBottom:4,opacity:.6,marginTop:4}}/>
          <div style={{width:"70%",height:3,background:"#ddd",borderRadius:1}}/>
        </div>
        {/* Style badge */}
        <div style={{position:"absolute",top:6,right:6,fontSize:"7px",background:a,color:"#fff",padding:"2px 5px",borderRadius:8,textTransform:"uppercase",letterSpacing:.5,opacity:.85}}>{styleLabels[tpl.style]||tpl.style}</div>
      </div>
      {/* Name label */}
      <div style={{background:selected?"rgba(255,60,95,.08)":"rgba(0,0,0,.04)",padding:"6px 8px",display:"flex",alignItems:"center",gap:5}}>
        <div style={{width:8,height:8,borderRadius:2,background:a,flexShrink:0}}/>
        <span style={{fontSize:".7em",fontWeight:700,color:selected?"#ff3c5f":"#333",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tpl.name}</span>
        {selected&&<span style={{marginLeft:"auto",color:"#ff3c5f",fontSize:".75em"}}>✓</span>}
      </div>
    </div>
  );
}

// ── Theme system ────────────────────────────────────────────
const LIGHT_VARS = `
  [data-theme="light"]{--R:#e8193c;--C:#0077cc;--G:#1a7a2e;--bg:#f2f3f7;--bg2:#ffffff;--card-bg:rgba(0,0,0,.03);--bdr:rgba(0,0,0,.08);--txt:#1a1a2e;--txt2:#556;--hdr:rgba(248,248,252,.95);--loader-txt:rgba(0,0,0,.3)}
  [data-theme="light"] body{background:#f2f3f7;color:#1a1a2e}
  [data-theme="light"] textarea,
  [data-theme="light"] input[type=text],
  [data-theme="light"] input[type=number],
  [data-theme="light"] select{background:#fff!important;border-color:rgba(0,0,0,.12)!important;color:#1a1a2e!important}
  [data-theme="light"] textarea::placeholder,[data-theme="light"] input::placeholder{color:rgba(0,0,0,.3)!important}
  [data-theme="light"] textarea:focus,[data-theme="light"] input:focus,[data-theme="light"] select:focus{border-color:rgba(232,25,60,.4)!important;background:#fff!important}
  [data-theme="light"] .card{background:#fff!important;border-color:rgba(0,0,0,.08)!important}
  [data-theme="light"] .btn{box-shadow:0 4px 16px rgba(232,25,60,.2)}
  [data-theme="light"] .btnG{background:rgba(0,0,0,.05)!important;border-color:rgba(0,0,0,.1)!important;color:rgba(0,0,0,.55)!important}
  [data-theme="light"] .btnG:hover{background:rgba(0,0,0,.09)!important;color:#1a1a2e!important}
  [data-theme="light"] .btnG.on{background:rgba(232,25,60,.08)!important;border-color:rgba(232,25,60,.3)!important;color:var(--R)!important}
  [data-theme="light"] .tab{color:rgba(0,0,0,.38)!important}
  [data-theme="light"] .tab.on{background:rgba(232,25,60,.1)!important;color:var(--R)!important}
  [data-theme="light"] .upload-zone{border-color:rgba(0,0,0,.14)!important}
  [data-theme="light"] .mhtitle{color:#1a1a2e!important}
  [data-theme="light"] .mhdesc{color:rgba(0,0,0,.55)!important}
  [data-theme="light"] .nx-loader .nx-label{color:rgba(0,0,0,.3)!important}
  [data-theme="light"] ::-webkit-scrollbar-thumb{background:var(--R)!important}
  /* Force all text inside cards and modules to dark in light mode */
  [data-theme="light"] .fade *:not(button):not(.btn):not(.btnG):not(.tag):not(a){color:inherit}
  [data-theme="light"] .fade{color:#1a1a2e}
  /* Chat bubbles */
  [data-theme="light"] .chat-user-bubble{background:linear-gradient(135deg,rgba(232,25,60,.13),rgba(120,20,200,.08))!important;border-color:rgba(232,25,60,.22)!important}
  [data-theme="light"] .chat-ai-bubble{background:rgba(0,0,0,.04)!important;border-color:rgba(0,0,0,.08)!important}
  /* Data Lab */
  [data-theme="light"] .data-report{color:#1a1a2e!important;background:#fff!important;border-color:rgba(0,0,0,.08)!important}
  [data-theme="light"] .data-clean-summary{background:rgba(0,150,80,.06)!important;border-color:rgba(0,150,80,.2)!important;color:#1a4a2e!important}
  [data-theme="light"] .data-clean-log{color:rgba(0,0,0,.6)!important}
  /* Study AI — quiz, tutor, stats */
  [data-theme="light"] .study-chat-bubble{color:#1a1a2e!important;background:rgba(0,0,0,.04)!important;border-color:rgba(0,0,0,.08)!important}
  [data-theme="light"] .quiz-q-text{color:#1a1a2e!important}
  [data-theme="light"] .quiz-explain{color:#1a4a5e!important;background:rgba(0,100,150,.06)!important;border-color:rgba(0,100,150,.18)!important}
  [data-theme="light"] .stat-val{color:#1a1a2e!important}
  [data-theme="light"] .quiz-stat-label{color:rgba(0,0,0,.55)!important}
  [data-theme="light"] .mic-btn{color:#111!important}
  /* Interview */
  [data-theme="light"] .interview-role-label{color:#1a1a2e!important}
  [data-theme="light"] .interview-ai-bubble{color:#1a1a2e!important;background:rgba(0,0,0,.04)!important;border-color:rgba(0,0,0,.09)!important}
  /* Quiz option buttons in light mode (unanswered state) */
  [data-theme="light"] button[style*="rgba(255,255,255,.04)"]{background:rgba(0,0,0,.04)!important;border-color:rgba(0,0,0,.12)!important;color:#1a1a2e!important}
  /* Drop zones in light mode */
  [data-theme="light"] .upload-zone{background:rgba(0,0,0,.02)!important;border-color:rgba(0,0,0,.15)!important}
  [data-theme="light"] .upload-zone *{color:#1a1a2e!important}
  /* Interview message bubbles */
  [data-theme="light"] .interview-user-bubble{background:linear-gradient(135deg,rgba(232,25,60,.12),rgba(120,20,200,.08))!important;border-color:rgba(232,25,60,.2)!important}
  [data-theme="light"] .interview-assistant-bubble{background:rgba(0,0,0,.04)!important;border-color:rgba(0,0,0,.09)!important;color:#1a1a2e!important}
  /* Any remaining white-on-dark text patterns */
  [data-theme="light"] [style*="color:"#fff""] { color: #1a1a2e !important; }
  [data-theme="light"] [style*="rgba(255,255,255,.7)"] { color: #1a1a2e !important; }
  [data-theme="light"] [style*="rgba(255,255,255,.55)"] { color: #556 !important; }
  [data-theme="light"] [style*="rgba(255,255,255,.45)"] { color: #556 !important; }
  [data-theme="light"] [style*="rgba(255,255,255,.35)"] { color: #778 !important; }
  [data-theme="light"] [style*="rgba(255,255,255,.25)"] { color: #99a !important; }
`;
const DARK_VARS = `
  [data-theme="dark"]{--R:#ff3c5f;--C:#00e5ff;--G:#a8ff78;--bg:#07070f;--bg2:#0f0f1e;--card-bg:rgba(255,255,255,.034);--bdr:rgba(255,255,255,.07);--txt:#e8e8f2;--txt2:#9999bb;--hdr:rgba(0,0,0,.55);--loader-txt:rgba(255,255,255,.3)}
`;

let _theme = localStorage.getItem("nx_theme") || "dark";
export function getTheme() { return _theme; }

// Header right slot — injected by RootApp so UserBadge sits INSIDE header
const MODS=[{id:"chat",label:"Assistant",icon:"◈"},{id:"code",label:"Code Lab",icon:"</>"},{id:"web",label:"Web Builder",icon:"⬡"},{id:"study",label:"Mentor",icon:"◉"},{id:"data",label:"Analyst",icon:"◎"}];
const OWNER_EMAIL_ADMIN = "rajarshipaul20@gmail.com";

export default function App() {
  const [mod,setMod]=useState("chat");
  const [theme,setTheme]=useState(()=>{
    const saved=localStorage.getItem("nx_theme")||"dark";
    document.documentElement.setAttribute("data-theme",saved);
    return saved;
  });
  const isDark = theme==="dark";
  const isOwner = isOwnerSession();

  // Soft animated background similar to Login page
  const bgCanvasRef = useRef(null);
  const bgAnimRef   = useRef(null);
  useEffect(()=>{
    const canvas = bgCanvasRef.current;
    if(!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const resize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);

    const COUNT = 60;
    const particles = Array.from({length:COUNT}, () => ({
      x: Math.random()*W,
      y: Math.random()*H,
      vx:(Math.random()-.5)*0.3,
      vy:(Math.random()-.5)*0.3,
      r: Math.random()*1.4+0.5,
      opacity: Math.random()*0.4+0.1,
    }));

    let mouse = {x:W/2,y:H/2};
    const onMouse = e => { mouse.x = e.clientX; mouse.y = e.clientY; };
    window.addEventListener("mousemove", onMouse);

    function draw(){
      ctx.clearRect(0,0,W,H);
      for(let i=0;i<particles.length;i++){
        for(let j=i+1;j<particles.length;j++){
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx*dx+dy*dy);
          if(dist<130){
            ctx.beginPath();
            ctx.moveTo(particles[i].x,particles[i].y);
            ctx.lineTo(particles[j].x,particles[j].y);
            ctx.strokeStyle=`rgba(255,60,95,${(1-dist/130)*0.06})`;
            ctx.lineWidth=0.4;
            ctx.stroke();
          }
        }
        const mdx = mouse.x - particles[i].x;
        const mdy = mouse.y - particles[i].y;
        const mdist = Math.sqrt(mdx*mdx+mdy*mdy);
        if(mdist<150){
          ctx.beginPath();
          ctx.moveTo(particles[i].x,particles[i].y);
          ctx.lineTo(mouse.x,mouse.y);
          ctx.strokeStyle=`rgba(155,29,255,${(1-mdist/150)*0.08})`;
          ctx.lineWidth=0.5;
          ctx.stroke();
        }
      }
      particles.forEach(p=>{
        p.x+=p.vx; p.y+=p.vy;
        if(p.x<0)p.x=W; if(p.x>W)p.x=0;
        if(p.y<0)p.y=H; if(p.y>H)p.y=0;
        ctx.beginPath();
        ctx.arc(p.x,p.y,p.r,0,Math.PI*2);
        ctx.fillStyle=`rgba(255,60,95,${p.opacity})`;
        ctx.fill();
      });
      bgAnimRef.current = requestAnimationFrame(draw);
    }
    draw();
    return ()=>{
      cancelAnimationFrame(bgAnimRef.current);
      window.removeEventListener("resize",resize);
      window.removeEventListener("mousemove",onMouse);
    };
  },[]);

  function toggleTheme() {
    const next = isDark?"light":"dark";
    setTheme(next);
    _theme = next;
    localStorage.setItem("nx_theme",next);
    document.documentElement.setAttribute("data-theme",next);
    document.body.style.background = next==="dark"?"#07070f":"#f2f3f7";
    document.body.style.color = next==="dark"?"#e8e8f2":"#1a1a2e";
  }

  const allMods = isOwner
    ? [...MODS, {id:"admin",label:"Admin",icon:"⚙"}]
    : MODS;

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",fontFamily:"'Syne','Segoe UI',sans-serif",color:"var(--txt)",display:"flex",flexDirection:"column",position:"relative",zIndex:0}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Syne+Mono&family=DM+Serif+Display:ital@0;1&display=swap');
        ${DARK_VARS}
        ${LIGHT_VARS}
        *{box-sizing:border-box}
        body{background:var(--bg);color:var(--txt);transition:background .25s,color .25s}
        ::-webkit-scrollbar{width:3px;background:transparent}::-webkit-scrollbar-thumb{background:var(--R);border-radius:3px}
        .card{background:var(--card-bg,rgba(255,255,255,.034));border:1px solid var(--bdr);border-radius:16px;padding:20px;transition:background .25s,border-color .25s}
        .btn{background:linear-gradient(135deg,var(--R),#9b1dff);border:none;color:#fff;padding:10px 20px;border-radius:9px;cursor:pointer;font-family:'Syne',sans-serif;font-size:.83em;font-weight:700;transition:all .22s;white-space:nowrap}
        .btn:hover{transform:translateY(-2px);box-shadow:0 8px 28px rgba(255,60,95,.32)}.btn:disabled{opacity:.4;transform:none;cursor:not-allowed}
        .btnG{background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);color:rgba(255,255,255,.62);padding:8px 14px;border-radius:8px;cursor:pointer;font-family:'Syne',sans-serif;font-size:.79em;font-weight:600;transition:all .18s;white-space:nowrap}
        .btnG:hover{background:rgba(255,255,255,.09);color:#fff}.btnG.on{background:rgba(255,60,95,.13);border-color:rgba(255,60,95,.4);color:var(--R)}.btnG:disabled{opacity:.35;cursor:not-allowed}
        textarea,input[type=text],input[type=number],select{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.09);border-radius:10px;color:var(--txt);padding:10px 13px;font-family:'Syne',sans-serif;font-size:.86em;outline:none;transition:border-color .18s,background .25s;resize:vertical;width:100%}
        textarea:focus,input:focus,select:focus{border-color:rgba(255,60,95,.5);background:rgba(255,255,255,.06)}
        textarea::placeholder,input::placeholder{color:rgba(255,255,255,.22)}
        .tag{background:rgba(255,60,95,.1);border:1px solid rgba(255,60,95,.28);color:var(--R);padding:2px 9px;border-radius:20px;font-size:.67em;letter-spacing:1.5px;text-transform:uppercase;font-weight:700}
        .fade{animation:fu .3s ease}@keyframes fu{from{opacity:0;transform:translateY(9px)}to{opacity:1;transform:translateY(0)}}
        @keyframes nxSpin{0%{transform:rotate(0deg) scale(1)}50%{transform:rotate(180deg) scale(1.2)}100%{transform:rotate(360deg) scale(1)}}
        @keyframes nxPulse{0%,100%{opacity:1;filter:drop-shadow(0 0 6px var(--R))}50%{opacity:.4;filter:drop-shadow(0 0 2px var(--R))}}
        .nx-loader{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px}
        .nx-loader .nx-x{font-size:2em;font-weight:800;color:var(--R);font-family:'Syne',sans-serif;animation:nxSpin 1.4s cubic-bezier(.6,0,.4,1) infinite}
        .nx-loader .nx-label{font-size:.65em;letter-spacing:3px;color:var(--loader-txt,rgba(255,255,255,.3));font-family:'Syne',sans-serif;animation:nxPulse 1.4s ease infinite}
        .dots span{display:none}
        .tabs{display:flex;gap:3px;background:rgba(255,255,255,.03);border:1px solid var(--bdr);border-radius:11px;padding:4px}
        .tab{flex:1;padding:7px 10px;border-radius:8px;border:none;background:transparent;color:var(--txt2);cursor:pointer;font-family:'Syne',sans-serif;font-size:.78em;font-weight:700;transition:all .18s;text-align:center}
        .tab.on{background:rgba(255,60,95,.15);color:var(--R)}
        .mhtitle{
          font-family:'Syne','Segoe UI',sans-serif;
          font-weight:800;
          font-size:1.9em;
          letter-spacing:-0.5px;
          color:var(--txt);
          text-shadow:0 0 16px rgba(255,60,95,0.45),0 0 32px rgba(155,29,255,0.25);
        }
        @keyframes recPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.3);opacity:.6}}.rec-dot{width:10px;height:10px;border-radius:50%;background:var(--R);animation:recPulse 1s infinite}
        .upload-zone{border:2px dashed rgba(255,255,255,.15);border-radius:12px;padding:20px;text-align:center;cursor:pointer;transition:all .2s}.upload-zone:hover,.upload-zone.drag{border-color:rgba(255,60,95,.5);background:rgba(255,60,95,.04)}
        .mic-btn{width:40px;height:40px;border-radius:50%;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1.1em;transition:all .2s;color:#fff}
        .mic-btn.idle{background:rgba(255,255,255,.07);box-shadow:none}.mic-btn.active{background:rgba(255,60,95,.3);box-shadow:0 0 14px rgba(255,60,95,.5)}

        /* ── Mobile Responsive ─────────────────────────── */
        @media(max-width:768px){
          .nx-main-content{padding:14px 12px!important}
          /* Header wraps into 2 rows */
          .nx-header{padding:8px 12px!important;gap:6px!important;flex-wrap:wrap!important;align-items:center!important}
          .nx-logo{font-size:1.05em!important;flex-shrink:0!important}
          .nx-divider{display:none!important}
          /* Row 1 right side: theme + dot — NO right padding needed, overlay is at bottom */
          .nx-header-right{
            padding-right:0!important;
            margin-left:auto!important;
            order:1!important;
            flex-shrink:0!important;
            /* Allow user badge injected into this slot to wrap below the theme toggle instead of overlapping nav/logo */
            display:flex!important;
            flex-wrap:wrap!important;
            justify-content:flex-end!important;
            gap:6px!important;
          }
          /* Row 2: nav full-width, horizontal scroll */
          .nx-nav{order:2!important;width:100%!important;flex-basis:100%!important;overflow-x:auto!important;-webkit-overflow-scrolling:touch!important;scrollbar-width:none!important;gap:3px!important;padding-bottom:2px!important;flex-shrink:0!important}
          .nx-nav::-webkit-scrollbar{display:none!important}
          .nx-nav button{padding:6px 10px!important;font-size:.7em!important;flex-shrink:0!important;white-space:nowrap!important}
          .card{padding:14px!important;border-radius:12px!important}
          .tabs{flex-wrap:wrap!important;gap:4px!important}
          .tab{flex:none!important;font-size:.72em!important;padding:6px 10px!important}
          .btn{padding:9px 14px!important;font-size:.8em!important}
          /* Mobile: user badge beside NEXUS (RootApp also sets this) */
          .nx-fixed-overlay{
            top:10px!important;
            left:104px!important;
            right:auto!important;
            width:auto!important;
            max-width:calc(100vw - 110px)!important;
            transform:none!important;
          }
        }
        @media(max-width:480px){
          .nx-main-content{padding:10px!important}
          .nx-header{padding:7px 10px!important}
          .nx-nav button{padding:5px 8px!important;font-size:.64em!important}
          .nx-logo{font-size:.9em!important}
          .card{padding:12px!important}
          .nx-fixed-overlay{top:9px!important;left:96px!important;max-width:calc(100vw - 110px)!important}
        }
      `}</style>
      {/* Background canvas + radial glows (behind all modules) */}
      <canvas
        ref={bgCanvasRef}
        style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,opacity:0.8}}
      />
      <div style={{position:"fixed",inset:0,pointerEvents:"none",overflow:"hidden",zIndex:0}}>
        <div style={{position:"absolute",width:600,height:600,borderRadius:"50%",background:"radial-gradient(circle,rgba(255,60,95,0.10) 0%,transparent 70%)",top:"-15%",left:"-10%",filter:"blur(1px)"}}/>
        <div style={{position:"absolute",width:520,height:520,borderRadius:"50%",background:"radial-gradient(circle,rgba(155,29,255,0.09) 0%,transparent 70%)",bottom:"-10%",right:"-5%",filter:"blur(1px)"}}/>
      </div>
      {/* Header */}
      <div className="nx-header" style={{background:"var(--hdr)",borderBottom:`1px solid var(--bdr)`,padding:"12px 24px",display:"flex",alignItems:"center",gap:14,backdropFilter:"blur(20px)",position:"sticky",top:0,zIndex:300}}>
        <div className="nx-logo" style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:"1.25em",color:"var(--txt)",flexShrink:0}}>NE<span style={{color:"var(--R)"}}>X</span>US</div>
        <div className="nx-divider" style={{width:1,height:18,background:"var(--bdr)",flexShrink:0}}/>
        <div className="nx-nav" style={{display:"flex",gap:3}}>
          {allMods.map(m=>(
            <button key={m.id} onClick={()=>setMod(m.id)} style={{background:mod===m.id?"rgba(255,60,95,.12)":"transparent",border:mod===m.id?"1px solid rgba(255,60,95,.35)":"1px solid transparent",color:mod===m.id?"var(--R)":"var(--txt2)",padding:"6px 13px",borderRadius:8,cursor:"pointer",fontFamily:"'Syne',sans-serif",fontSize:".78em",fontWeight:700,transition:"all .18s",display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontFamily:"monospace"}}>{m.icon}</span>{m.label}
            </button>
          ))}
        </div>
        <div className="nx-header-right" style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10,paddingRight:260,flexShrink:0}}>
          {/* Theme toggle */}
          <button onClick={toggleTheme} title={isDark?"Switch to Light Mode":"Switch to Dark Mode"}
            style={{width:34,height:34,borderRadius:8,border:`1px solid var(--bdr)`,background:"var(--card-bg,rgba(255,255,255,.034))",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1em",transition:"all .2s"}}>
            {isDark?"☀️":"🌙"}
          </button>
          <div style={{width:6,height:6,borderRadius:"50%",background:"#00ff88",boxShadow:"0 0 6px #00ff88"}}/>
        </div>
      </div>
      <div className="nx-main-content" style={{flex:1,overflow:"auto",padding:"24px 28px",position:"relative",zIndex:1}}>
        <div className="fade" key={mod}>
          <ErrorBoundary key={mod}>
            {mod==="chat"  && <ChatModule/>}
            {mod==="code"  && <CodeModule/>}
            {mod==="web"   && <WebModule/>}
            {mod==="study" && <StudyModule/>}
            {mod==="data"  && <DataModule/>}
            {mod==="admin" && <AdminModule/>}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

function MH({icon,title,desc,right}) {
  return (
    <div style={{marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:3}}>
            <span style={{fontFamily:"monospace",fontSize:"1.2em",color:"var(--R)"}}>{icon}</span>
            <span className="mhtitle">{title}</span>
          </div>
          <div className="mhdesc" style={{fontSize:".77em",color:"rgba(255,255,255,.28)",paddingLeft:36}}>{desc}</div>
        </div>
        {right}
      </div>
      <div style={{height:1,background:"linear-gradient(90deg,rgba(255,60,95,.4),transparent)",marginTop:14}}/>
    </div>
  );
}

// ── Reusable voice input hook ──────────────────────────────
function useSpeechInput(onTranscript) {
  const [listening,setLis]=useState(false);
  const recRef=useRef(null);
  function toggle() {
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){alert("Voice input requires Chrome browser.");return;}
    if(listening){recRef.current?.stop();setLis(false);return;}
    const r=new SR(); r.lang="en-US"; r.continuous=false; r.interimResults=false;
    r.onstart=()=>setLis(true); r.onend=()=>setLis(false); r.onerror=()=>setLis(false);
    r.onresult=e=>onTranscript(e.results[0][0].transcript);
    recRef.current=r; r.start();
  }
  return {listening, toggle};
}

function MicButton({listening, onToggle}) {
  return (
    <button onClick={onToggle} className={`mic-btn ${listening?"active":"idle"}`} title={listening?"Stop listening":"Start voice input"}>
      {listening ? (
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"
          style={{display:"block"}}>
          <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor"/>
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false"
          style={{display:"block"}}>
          <path d="M12 14a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v4a3 3 0 0 0 3 3Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19 11a7 7 0 0 1-14 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 18v3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 21h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════
   ASSISTANT — Chat · PDF · Real PPTX
══════════════════════════════════════════════════════════ */
// ══════════════════════════════════════════════════════════
//  ADMIN MODULE — Owner only
// ══════════════════════════════════════════════════════════
function AdminModule() {
  const [users,setUsers]=useState([]);
  const [loading,setLoading]=useState(false);
  const [top7,setTop7]=useState(null);
  const [top7Err,setTop7Err]=useState("");
  const [upgradeEmail,setUpgradeEmail]=useState("");
  const [upgradePlan,setUpgradePlan]=useState("paid");
  const [msg,setMsg]=useState("");
  const [msgType,setMsgType]=useState("ok");

  async function loadTop7() {
    setTop7Err("");
    try {
      const res=await fetch("/api/admin/top-module?days=7",{headers:{"x-session-token":_sessionToken}});
      const d=await res.json();
      if(res.ok) setTop7(d.top||null);
      else setTop7Err(d.error||"Failed to load analytics");
    } catch(e){setTop7Err(e.message);}
  }

  async function loadUsers() {
    setLoading(true);
    try {
      const res=await fetch("/api/admin/users",{headers:{"x-session-token":_sessionToken}});
      const d=await res.json();
      if(res.ok) setUsers(d.users||[]);
      else setMsg(d.error||"Failed to load users");
    } catch(e){setMsg(e.message);}
    setLoading(false);
  }

  useEffect(()=>{loadUsers(); loadTop7();},[]);

  async function upgradeUser(e) {
    e.preventDefault();
    if(!upgradeEmail.trim()) return;
    setLoading(true); setMsg("");
    try {
      const res=await fetch("/api/admin/upgrade",{
        method:"POST",
        headers:{"Content-Type":"application/json","x-session-token":_sessionToken},
        body:JSON.stringify({email:upgradeEmail.trim(),plan:upgradePlan}),
      });
      const d=await res.json();
      if(res.ok){
        setMsg(d.message||"User upgraded successfully");
        setMsgType("ok");
        setUpgradeEmail("");
        loadUsers();
      } else {
        setMsg(d.error||"Upgrade failed");
        setMsgType("err");
      }
    } catch(e){setMsg(e.message);setMsgType("err");}
    setLoading(false);
  }

  async function deleteUser(email, name) {
    if(!window.confirm(`Delete "${name}" (${email})?\n\nThis will permanently remove the account and all usage data. This cannot be undone.`)) return;
    setLoading(true); setMsg("");
    try {
      const res=await fetch("/api/admin/delete",{
        method:"POST",
        headers:{"Content-Type":"application/json","x-session-token":_sessionToken},
        body:JSON.stringify({email}),
      });
      const d=await res.json();
      if(res.ok){
        setMsg(`✓ ${email} deleted`);
        setMsgType("ok");
        loadUsers();
      } else {
        setMsg(d.error||"Delete failed");
        setMsgType("err");
      }
    } catch(e){setMsg(e.message);setMsgType("err");}
    setLoading(false);
  }

  const planColor={free:"rgba(255,255,255,.3)",paid:"#a8ff78",owner:"#ffd166"};

  return (
    <div>
      <MH icon="⚙" title="Admin Panel" desc="Owner only — manage users and plans"/>

      {/* 7-day top module */}
      <div className="card" style={{marginBottom:16,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:".7em",letterSpacing:"2px",color:"var(--R)",fontWeight:800,marginBottom:6}}>📊 TOP MODULE (LAST 7 DAYS)</div>
          {top7Err ? (
            <div style={{fontSize:".8em",color:"var(--R)"}}>⚠ {top7Err}</div>
          ) : top7 ? (
            <div style={{fontSize:".92em",fontWeight:800,color:"var(--txt)"}}>
              {String(top7.action||"").toUpperCase()} <span style={{fontSize:".8em",fontWeight:700,color:"var(--txt2)"}}>· {top7.count} uses</span>
            </div>
          ) : (
            <div style={{fontSize:".8em",color:"var(--txt2)"}}>No usage recorded yet.</div>
          )}
        </div>
        <button className="btnG" style={{fontSize:".72em"}} onClick={loadTop7} disabled={loading}>↺ Refresh</button>
      </div>

      {/* Upgrade form */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{fontSize:".7em",letterSpacing:"2px",color:"var(--R)",marginBottom:12,fontWeight:700}}>⚡ UPGRADE USER PLAN</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{flex:2,minWidth:200}}>
            <div style={{fontSize:".72em",color:"var(--txt2)",marginBottom:4}}>User Email</div>
            <input type="text" value={upgradeEmail} onChange={e=>setUpgradeEmail(e.target.value)}
              placeholder="user@email.com" style={{width:"100%"}}/>
          </div>
          <div style={{flex:1,minWidth:120}}>
            <div style={{fontSize:".72em",color:"var(--txt2)",marginBottom:4}}>Plan</div>
            <select value={upgradePlan} onChange={e=>setUpgradePlan(e.target.value)} style={{width:"100%"}}>
              <option value="paid">💎 Pro</option>
              <option value="free">🆓 Free</option>
              <option value="owner">👑 Owner</option>
            </select>
          </div>
          <button className="btn" onClick={upgradeUser} disabled={loading||!upgradeEmail.trim()} style={{minWidth:110}}>
            {loading?"⟳ Upgrading...":"✦ Upgrade"}
          </button>
        </div>
        {msg&&<div style={{marginTop:10,fontSize:".78em",padding:"8px 12px",borderRadius:8,background:msgType==="ok"?"rgba(168,255,120,.1)":"rgba(255,60,95,.1)",border:`1px solid ${msgType==="ok"?"rgba(168,255,120,.3)":"rgba(255,60,95,.3)"}`,color:msgType==="ok"?"#a8ff78":"#ff8090"}}>
          {msgType==="ok"?"✓":"⚠"} {msg}
        </div>}
        <div style={{marginTop:10,fontSize:".7em",color:"var(--txt2)"}}>
          💡 After upgrading, an email is automatically sent to the user confirming their Pro access.
        </div>
      </div>

      {/* Users list */}
      <div className="card">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontSize:".7em",letterSpacing:"2px",color:"var(--R)",fontWeight:700}}>👥 ALL USERS ({users.length})</div>
          <button className="btnG" style={{fontSize:".72em"}} onClick={loadUsers} disabled={loading}>↺ Refresh</button>
        </div>
        {loading&&<div className="nx-loader" style={{padding:20}}><div className="nx-x">X</div><div className="nx-label">LOADING</div></div>}
        {!loading&&users.length===0&&<div style={{textAlign:"center",color:"var(--txt2)",fontSize:".8em",padding:20}}>No users yet</div>}
        {!loading&&users.map(u=>(
          <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid var(--bdr)"}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,var(--R),#9b1dff)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:".82em",fontWeight:700,color:"#fff",flexShrink:0}}>
              {u.name?.[0]?.toUpperCase()||"?"}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:".82em",fontWeight:700,color:"var(--txt)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</div>
              <div style={{fontSize:".7em",color:"var(--txt2)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}</div>
            </div>
            <div style={{fontSize:".68em",fontWeight:700,color:planColor[u.plan]||"var(--txt2)",flexShrink:0}}>
              {u.plan==="owner"?"👑 OWNER":u.plan==="paid"?"💎 PRO":"🆓 FREE"}
            </div>
            <button className="btnG" style={{fontSize:".65em",padding:"4px 8px",flexShrink:0}}
              onClick={()=>{setUpgradeEmail(u.email);setUpgradePlan(u.plan==="paid"?"free":"paid");}}>
              {u.plan==="paid"?"↓ Downgrade":"↑ Upgrade"}
            </button>
            <button className="btnG" style={{fontSize:".65em",padding:"4px 8px",flexShrink:0,borderColor:"rgba(255,60,95,.3)",color:"var(--R)"}}
              onClick={()=>deleteUser(u.email,u.name)}>
              🗑
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatModule() {
  const [msgs,setMsgs]=useState([{role:"assistant",content:"Hello! I'm **NEXUS Assistant**.\n\nModes:\n- **Chat** — general conversation\n- **PDF** — professional print-ready documents\n- **Presentation** — download as real **.pptx** (opens in PowerPoint / Google Slides)",ts:Date.now()}]);
  const [input,setInput]=useState(""); const [mode,setMode]=useState("chat");
  const [docTitle,setTitle]=useState(""); const [nSlides,setN]=useState(10); const [pptTheme,setTheme]=useState("dark");
  const [loading,setLoad]=useState(false); const [pdfReady,setPDF]=useState(null); const [pptReady,setPPT]=useState(null);
  const [pptxBusy,setPX]=useState(false); const [err,setErr]=useState(""); const [pdfPages,setPdfPages]=useState(2);
  const [fileCtx,setFileCtx]=useState(null); const [fileLoad,setFLoad]=useState(false);
  const fileRef=useRef(null);
  const endRef=useRef();
  useEffect(()=>endRef.current?.scrollIntoView({behavior:"smooth"}),[msgs]);
  const {listening,toggle}=useSpeechInput(t=>setInput(p=>p+(p?" ":"")+t));

  async function handleFile(file) {
    if(!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if(!["pdf","docx","jpg","jpeg"].includes(ext)) {
      setErr("Only PDF, DOCX, and JPG files are supported here.");
      return;
    }
    setFLoad(true); setErr("");
    try{
      let extractedText="";
      if(ext==="pdf"){
        const b64=await fileToBase64(file);
        const txt=await callAIMulti(
          [{type:"document",source:{type:"base64",media_type:"application/pdf",data:b64}},
           {type:"text",text:"Extract all readable text from this PDF. Return only the plain text content."}],
          "You accurately extract all text from the PDF.",8192,"chat");
        extractedText=txt;
      } else if(ext==="docx"){
        const b64=await fileToBase64(file);
        const txt=await callAIMulti(
          [{type:"document",source:{type:"base64",media_type:"application/vnd.openxmlformats-officedocument.wordprocessingml.document",data:b64}},
           {type:"text",text:"Extract all readable text from this Word document. Return only the plain text content."}],
          "You accurately extract all text from the DOCX.",8192,"chat");
        extractedText=txt;
      } else {
        const b64=await fileToBase64(file);
        const txt=await callAIMulti(
          [{type:"image",source:{type:"base64",media_type:file.type||"image/jpeg",data:b64}},
           {type:"text",text:"Transcribe all text visible in this image and, if helpful, add a short description. Return plain text only."}],
          "You accurately transcribe all visible text from the image.",4096,"chat");
        extractedText=txt;
      }
      if(!extractedText || extractedText.trim().length<20){
        throw new Error("Could not read enough text from this file. Try another file.");
      }
      const ctx={name:file.name,text:extractedText.slice(0,16000)};
      setFileCtx(ctx);
      setMsgs(m=>[...m,{role:"assistant",content:`📎 **File attached:** "${file.name}".\n\nI've read this file — you can now ask questions about it (in Chat mode) and I'll answer using its contents.`,ts:Date.now()}]);
    } catch(e){
      setErr(e.message||"File analysis failed.");
    } finally{
      setFLoad(false);
    }
  }

  async function send() {
    if(!input.trim()||loading) return;
    const um={role:"user",content:input,ts:Date.now()};
    const hist=[...msgs,um]; setMsgs(hist); setInput(""); setLoad(true); setErr(""); setPDF(null); setPPT(null);
    try {
      if(mode==="pdf") {
        const t=docTitle.trim()||input.slice(0,70);
        const wordTarget = pdfPages * 500;
        const content=await callAI([{role:"user",content:`Write a comprehensive professional document titled: "${t}"\nNotes: ${input}\n\nUse ## sections, **bold** key terms, - bullet lists. Target approximately ${wordTarget} words (for ~${pdfPages} printed pages). End with Key Takeaways.`}],
          "You are a world-class professional writer. Produce richly detailed, precise documents.",8192,"pdf");
        setPDF({t,content});
        setMsgs([...hist,{role:"assistant",content:`✅ **PDF Ready:** "${t}"\n\n~${content.split(" ").length.toLocaleString()} words. Click **Export PDF** to download.`,ts:Date.now()}]);
      } else if(mode==="ppt") {
        const t=docTitle.trim()||input.slice(0,70);
        const raw=await callAI([{role:"user",content:`Create a ${nSlides}-slide presentation on: "${t}"\nContext: ${input}\n\nReturn ONLY a raw JSON array (no markdown fences):\n[{"title":"Slide Title","body":"Point 1\\nPoint 2\\nPoint 3\\nPoint 4"},...]`}],
          "Return only a raw JSON array. No markdown, no backticks, no explanation.",2048,"ppt");
        const slides=JSON.parse(raw.replace(/```json?\n?/g,"").replace(/```/g,"").trim());
        setPPT({t,slides});
        setMsgs([...hist,{role:"assistant",content:`✅ **Presentation Ready:** "${t}" — ${slides.length} slides\n\nClick **⬇ Download PPTX** to get a real PowerPoint file.`,ts:Date.now()}]);
      } else {
        const baseSystem="You are NEXUS, a brilliant AI assistant. Be insightful, precise, helpful. Use markdown.";
        const system=fileCtx
          ? `${baseSystem}\n\nYou also have access to this document context from a file the user attached ("${fileCtx.name}"):\n\n${fileCtx.text}\n\nWhen the user asks about the document, answer using this as the primary source. If the question is unrelated, answer normally and ignore the document.`
          : baseSystem;
        const reply=await callAI(hist.map(m=>({role:m.role,content:m.content})),system,4096,"chat");
        setMsgs([...hist,{role:"assistant",content:reply,ts:Date.now()}]);
      }
    } catch(e){setErr(e.message);setMsgs([...hist,{role:"assistant",content:`⚠ ${e.message}`,ts:Date.now()}]);}
    finally{setLoad(false);}
  }

  return (
    <div style={{maxWidth:860,margin:"0 auto"}}>
      <MH icon="◈" title="Assistant" desc="Chat · PDF documents · Real .pptx presentations (PowerPoint-ready)" right={<RateBadge action={mode==="chat"?"chat":mode}/>}/>
      <div style={{display:"flex",gap:7,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        {[["chat","◈ Chat"],["pdf","📄 PDF"],["ppt","🎞 Presentation"]].map(([id,lb])=>(
          <button key={id} className={`btnG ${mode===id?"on":""}`} onClick={()=>setMode(id)}>{lb}</button>
        ))}
        {mode!=="chat"&&<input type="text" value={docTitle} onChange={e=>setTitle(e.target.value)} placeholder={mode==="pdf"?"Document title...":"Presentation title..."} style={{flex:1,minWidth:160}}/>}
        {mode==="pdf"&&
          <div style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:9,padding:"0 11px",height:38,flexShrink:0}}>
            <span style={{fontSize:".73em",color:"var(--txt2)"}}>Pages</span>
            <input type="number" value={pdfPages} onChange={e=>setPdfPages(Math.max(1,Math.min(20,+e.target.value)))} min={1} max={20} style={{width:38,background:"transparent",border:"none",padding:0,color:"var(--txt)",fontSize:".86em"}}/>
          </div>
        }
        {mode==="ppt"&&<>
          <div style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:9,padding:"0 11px",height:38}}>
            <span style={{fontSize:".73em",color:"var(--txt2)"}}>Slides</span>
            <input type="number" value={nSlides} onChange={e=>setN(+e.target.value)} min={4} max={20} style={{width:46,background:"transparent",border:"none",padding:0,color:"var(--txt)",fontSize:".86em"}}/>
          </div>
          <select value={pptTheme} onChange={e=>setTheme(e.target.value)} style={{width:105}}>
            <option value="dark">Dark</option><option value="blue">Blue</option>
            <option value="green">Green</option><option value="purple">Purple</option><option value="light">Light</option>
          </select>
        </>}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:11,maxHeight:"52vh",overflowY:"auto",marginBottom:12,paddingRight:2}}>
        {msgs.map((m,i)=>(
          <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
            <div className={m.role==="user"?"chat-user-bubble":"chat-ai-bubble"} style={{maxWidth:"78%",background:m.role==="user"?"linear-gradient(135deg,rgba(255,60,95,.22),rgba(155,29,255,.16))":"rgba(255,255,255,.04)",border:`1px solid ${m.role==="user"?"rgba(255,60,95,.22)":"rgba(255,255,255,.07)"}`,borderRadius:m.role==="user"?"15px 15px 4px 15px":"4px 15px 15px 15px",padding:"11px 15px",fontSize:".86em",lineHeight:1.72,color:"var(--txt)"}}>
              {m.role==="assistant"&&<div style={{fontSize:".62em",letterSpacing:"1.5px",color:"var(--R)",marginBottom:4,fontWeight:700}}>NEXUS</div>}
              <div dangerouslySetInnerHTML={{__html:md(m.content)}}/>
            </div>
          </div>
        ))}
        {loading&&<div style={{display:"flex"}}><div className="nx-loader" style={{padding:"11px 17px"}}><div className="nx-x" style={{fontSize:"1.3em"}}>X</div></div></div>}
        <div ref={endRef}/>
      </div>
      {pdfReady&&<div className="card" style={{marginBottom:10,display:"flex",alignItems:"center",gap:10,padding:"11px 15px"}}>
        <span style={{fontSize:".8em",color:"var(--txt2)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📄 {pdfReady.t}</span>
        <button className="btn" onClick={()=>exportPDF(pdfReady.t,pdfReady.content)}>⬇ Export PDF</button>
      </div>}
      {pptReady&&<div className="card" style={{marginBottom:10,display:"flex",alignItems:"center",gap:10,padding:"11px 15px",flexWrap:"wrap"}}>
        <span style={{fontSize:".8em",color:"var(--txt2)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🎞 {pptReady.t} — {pptReady.slides.length} slides</span>
        <select value={pptTheme} onChange={e=>setTheme(e.target.value)} style={{width:100,padding:"6px 8px"}}>
          <option value="dark">Dark</option><option value="blue">Blue</option><option value="green">Green</option><option value="purple">Purple</option><option value="light">Light</option>
        </select>
        <button className="btn" disabled={pptxBusy} onClick={async()=>{setPX(true);try{await downloadPPTX(pptReady.t,pptReady.slides,pptTheme);}catch(e){setErr(e.message);}finally{setPX(false);}}}>
          {pptxBusy?"⟳ Generating...":"⬇ Download PPTX"}
        </button>
      </div>}
      {err&&<div style={{background:"rgba(255,60,95,.1)",border:"1px solid rgba(255,60,95,.3)",borderRadius:10,padding:"10px 14px",marginBottom:10,fontSize:".82em",color:"#ff8090"}}>⚠ {err}</div>}
      <div style={{display:"flex",gap:10,alignItems:"flex-end",marginTop:8}}>
        <div style={{position:"relative",flex:1,display:"flex",alignItems:"center",gap:6}}>
          <textarea
            value={input}
            onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send()}}}
            placeholder={mode==="pdf"?"Describe the document...":mode==="ppt"?"What is this presentation about?":"Ask anything... (Enter to send · Shift+Enter newline)"}
            style={{
              width:"100%",
              minHeight:76,
              maxHeight:180,
              paddingLeft:18,
              paddingRight:70,
              paddingTop:14,
              paddingBottom:14,
              borderRadius:999,
              resize:"none",
            }}
          />
          {/* Mic + Attach, sitting at right edge of the pill */}
          <div style={{position:"absolute",right:8,bottom:8,display:"flex",alignItems:"center",gap:6}}>
            <MicButton listening={listening} onToggle={toggle}/>
            <button
              type="button"
              onClick={()=>fileRef.current?.click()}
              style={{
                width:40,
                height:40,
                borderRadius:"50%",
                border:"none",
                background:"rgba(255,255,255,0.06)",
                color:"var(--txt2)",
                cursor:"pointer",
                fontSize:"1.3em",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                padding:0,
              }}
              title="Attach PDF / DOCX / JPG"
            >
              📎
            </button>
          </div>
          {fileLoad && (
            <div style={{position:"absolute",left:22,bottom:10,fontSize:".72em",color:"var(--txt2)"}}>
              Analyzing…
            </div>
          )}
          {fileCtx && !fileLoad && (
            <div style={{position:"absolute",left:22,bottom:10,fontSize:".72em",color:"var(--txt2)",maxWidth:"55%",whiteSpace:"nowrap",textOverflow:"ellipsis",overflow:"hidden"}}>
              Attached: {fileCtx.name}
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.jpg,.jpeg"
            style={{display:"none"}}
            onChange={e=>{const f=e.target.files?.[0]; if(f) handleFile(f); e.target.value="";}}
          />
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,alignSelf:"flex-end"}}>
          <button className="btn" onClick={send} disabled={loading} style={{height:46,minWidth:80,padding:"0 16px",fontSize:".85em"}}>{loading?"...":mode==="pdf"?"Gen":mode==="ppt"?"Create":"↑"}</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   CODE LAB
══════════════════════════════════════════════════════════ */
function CodeModule() {
  const [prompt,setPr]=useState(""); const [lang,setLang]=useState("python");
  const [code,setCode]=useState(""); const [exp,setExp]=useState("");
  const [output,setOut]=useState(""); const [loading,setLoad]=useState(false);
  const [running,setRun]=useState(false); const [tab,setTab]=useState("code");
  const LANGS=["python","javascript","typescript","react","html","java","go","rust","sql","bash","c++","css"];

  async function generate() {
    if(!prompt.trim()||loading)return; setLoad(true); setCode(""); setExp(""); setOut(""); setTab("code");
    try {
      const raw=await callAI([{role:"user",content:`Write complete, production-quality ${lang} code for:\n"${prompt}"\n\nFormat EXACTLY:\n<code>\n[complete code]\n</code>\n<explanation>\n[how it works]\n</explanation>`}],
        "You are a senior software engineer. Write exemplary production-ready code.",4096,"code");
      const cm=raw.match(/<code>([\s\S]*?)<\/code>/); const em=raw.match(/<explanation>([\s\S]*?)<\/explanation>/);
      setCode(cm?cm[1].trim():raw); setExp(em?em[1].trim():"");
    } finally{setLoad(false);}
  }
  async function runCode() {
    if(!code||running)return; setRun(true); setOut(""); setTab("output");
    try{const r=await callAI([{role:"user",content:`Simulate running this ${lang} code. Show only terminal output:\n\`\`\`${lang}\n${code}\n\`\`\``}],"Show terminal output only.",1024,"code");setOut(r);}
    finally{setRun(false);}
  }
  return (
    <div>
      <MH icon="</>" title="Code Lab" desc="Generate · Edit · Simulate" right={<RateBadge action="code"/>}/>
      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
        {LANGS.map(l=><button key={l} className={`btnG ${lang===l?"on":""}`} onClick={()=>setLang(l)} style={{padding:"5px 11px",fontSize:".75em"}}>{l}</button>)}
      </div>
      <div style={{display:"flex",gap:10,marginBottom:10}}>
        <textarea value={prompt} onChange={e=>setPr(e.target.value)} placeholder={`Describe what to build in ${lang}...`} style={{flex:1,minHeight:74}}/>
        <button className="btn" onClick={generate} disabled={loading} style={{alignSelf:"flex-end",minWidth:110}}>{loading?"⟳ Writing...":"⚡ Generate"}</button>
      </div>
      {code&&<div className="card fade">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div className="tabs" style={{flex:1,marginRight:9}}>
            <button className={`tab ${tab==="code"?"on":""}`} onClick={()=>setTab("code")}>Code</button>
            <button className={`tab ${tab==="explain"?"on":""}`} onClick={()=>setTab("explain")}>Explanation</button>
            <button className={`tab ${tab==="output"?"on":""}`} onClick={()=>setTab("output")}>{running?"Running…":"Output"}</button>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button className="btnG" onClick={()=>navigator.clipboard.writeText(code)} style={{fontSize:".74em"}}>⎘ Copy</button>
            <button className="btn" onClick={runCode} disabled={running} style={{fontSize:".78em",padding:"7px 12px"}}>{running?"…":"▶ Run"}</button>
          </div>
        </div>
        {tab==="code"&&<textarea value={code} onChange={e=>setCode(e.target.value)} style={{width:"100%",minHeight:320,fontFamily:"'Syne Mono',monospace",fontSize:".82em",lineHeight:1.65,background:"#09091a",border:"1px solid var(--bdr)",borderRadius:10,color:"#a8d8a8",padding:"16px 18px"}}/>}
        {tab==="explain"&&<div style={{fontSize:".85em",lineHeight:1.85,color:"#bac2de",whiteSpace:"pre-wrap"}}>{exp||"No explanation."}</div>}
        {tab==="output"&&<div style={{background:"#040410",border:"1px solid rgba(255,255,255,.06)",borderRadius:10,padding:"16px 18px",minHeight:180}}>
          <div style={{fontSize:".62em",color:"rgba(255,255,255,.2)",letterSpacing:"2px",marginBottom:10,fontFamily:"monospace"}}>● TERMINAL</div>
          {running?<div className="nx-loader"><div className="nx-x">X</div><div className="nx-label">PROCESSING</div></div>:<pre style={{fontFamily:"'Syne Mono',monospace",fontSize:".83em",color:"#92d892",lineHeight:1.72,whiteSpace:"pre-wrap"}}>{output||"Press ▶ Run"}</pre>}
        </div>}
      </div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   WEB BUILDER — Fully functional + Unsplash images
══════════════════════════════════════════════════════════ */
function WebModule() {
  const [desc,setDesc]=useState(""); const [siteType,setST]=useState("landing"); const [style,setStyle]=useState("modern-dark");
  const [html,setHtml]=useState(""); const [loading,setLoad]=useState(false); const [pview,setPview]=useState("preview");
  const [refine,setRef]=useState(""); const [refining,setRfn]=useState(false); const [hist,setHist]=useState([]);
  const [blobUrl,setBlob]=useState(""); const [errMsg,setErr]=useState("");
  const iframeRef=useRef(null);

  useEffect(()=>{
    if(!html){setBlob("");return;}
    const blob=new Blob([html],{type:"text/html"}); const url=URL.createObjectURL(blob); setBlob(url);
    return()=>URL.revokeObjectURL(url);
  },[html]);
  useEffect(()=>{if(blobUrl&&iframeRef.current)iframeRef.current.src=blobUrl;},[blobUrl]);

  const TYPES=[{id:"landing",lb:"Landing Page"},{id:"ecommerce",lb:"E-Commerce"},{id:"dashboard",lb:"Dashboard"},{id:"portfolio",lb:"Portfolio"},{id:"saas",lb:"SaaS App"},{id:"restaurant",lb:"Restaurant"},{id:"blog",lb:"Blog"},{id:"analytics",lb:"Analytics"},{id:"realworld",lb:"Real World App"}];
  const STYLES=["modern-dark","light-minimal","glassmorphism","bold-editorial","brutalist","cyberpunk","luxury","playful"];

  const SYS=`You are an elite front-end developer and UI/UX designer who builds stunning, award-winning websites.
Output ONLY raw HTML — no markdown, no backticks, no explanation, no comments outside the HTML.
Start with <!DOCTYPE html> and end with </html>. Nothing else before or after.
All CSS in <style>, all JS in <script>. One single self-contained file.
DESIGN PHILOSOPHY: Every site you build looks like it was made by a top agency. Use bold typography, smooth animations, micro-interactions, and intentional whitespace. Never build plain or generic-looking sites.`;

  // Extract specific product/subject keywords for accurate image search
  function getImageKeywords(description, type) {
    const desc = description.toLowerCase().replace(/[^a-z0-9 ]/g," ");
    const SKIP = new Set(["with","that","this","have","will","from","into","your","build","make","want","need","page","site","website","create","design","modern","clean","luxury","premium","professional","beautiful","elegant","simple","minimal","dark","light","black","white"]);
    // Get meaningful subject words
    const words = desc.split(" ").filter(w=>w.length>2 && !SKIP.has(w));
    // Specific product keywords get priority
    const PRODUCT_MAP = {
      // Car brands — very specific so Pexels returns correct brand
      "bmw":"BMW car automobile M3","mercedes":"Mercedes Benz luxury car","audi":"Audi car automobile",
      "porsche":"Porsche sports car","ferrari":"Ferrari sports car red","lamborghini":"Lamborghini supercar",
      "tesla":"Tesla electric car","ford":"Ford car automobile","toyota":"Toyota car","honda":"Honda car",
      "volkswagen":"Volkswagen car","rolls royce":"Rolls Royce luxury car",
      // Watches
      "rolex":"Rolex watch luxury gold","watch":"luxury watch timepiece close up","watches":"luxury watches collection",
      "omega":"Omega watch luxury","casio":"Casio watch digital","seiko":"Seiko watch",
      // Food - specific
      "samosa":"samosa indian street food","pizza":"pizza italian food","burger":"burger fast food juicy",
      "sushi":"sushi japanese food rolls","biryani":"biryani indian rice dish","pasta":"pasta italian food",
      "coffee":"coffee espresso barista","cafe":"coffee shop cafe interior","bakery":"bakery bread pastry",
      "restaurant":"restaurant food fine dining","food":"food plating restaurant",
      // Fashion
      "shoe":"shoes sneakers footwear","shoes":"shoes sneakers collection","sneaker":"sneakers Nike Adidas",
      "jewelry":"jewelry diamonds necklace","ring":"diamond ring engagement","necklace":"gold necklace jewelry",
      "fashion":"fashion clothing runway","clothing":"clothing apparel fashion","dress":"dress fashion elegant",
      // Tech
      "laptop":"laptop computer macbook","phone":"smartphone iPhone","iphone":"iPhone Apple smartphone",
      "tech":"technology gadgets modern","app":"mobile app UI screen","software":"software technology dashboard",
      // Lifestyle
      "gym":"gym fitness workout equipment","yoga":"yoga wellness meditation","spa":"spa wellness luxury massage",
      "hotel":"hotel luxury room interior","travel":"travel destination aerial landscape","beach":"beach ocean tropical waves",
      // Other
      "furniture":"furniture interior design modern","home":"interior design home living room",
      "flower":"flowers floral bouquet colorful","plant":"indoor plants nature green",
      "book":"books library reading","art":"art gallery painting modern","music":"music concert live performance",
      "wedding":"wedding ceremony elegant flowers","real estate":"real estate house architecture",
      "motorcycle":"motorcycle bike road","bike":"bicycle cycling outdoor",
    };
    for(const [k,v] of Object.entries(PRODUCT_MAP)) {
      if(desc.includes(k)) return v.replace(/ /g,"+");
    }
    const subject = words.slice(0,3).join("+");
    const fallbacks = {
      ecommerce:"product store shopping", restaurant:"food restaurant dining",
      portfolio:"creative design workspace", saas:"technology software dashboard",
      dashboard:"analytics data office", blog:"writing desk coffee laptop",
      landing:"business team professional", analytics:"data charts analytics",
    };
    return subject || (fallbacks[type]||"professional business").replace(/ /g,"+");
  }

  async function build() {
    if(!desc.trim()||loading) return;
    setLoad(true); setHtml(""); setBlob(""); setHist([]); setPview("preview"); setErr("");
    try {
      const keywords = getImageKeywords(desc, siteType);
      const kw = keywords.replace(/\+/g, " ");  // space-separated for Pexels query
      const kwComma = keywords.replace(/\+/g, ","); // comma for other uses

      // Pre-fetch real Pexels image URLs before building — inject directly into prompt
      let imgInstructions = "";
      try {
        const pRes = await fetch(`/api/pexels?q=${encodeURIComponent(kw)}&n=15`);
        const pData = await pRes.json();
        if (pData.urls && pData.urls.length > 0) {
          const large  = pData.urls.map((u,i) => `IMG_${i+1}: ${u.large}`).join("\n");
          const medium = pData.urls.map((u,i) => `THUMB_${i+1}: ${u.thumb}`).join("\n");
          imgInstructions = `
IMAGE RULES — USE ONLY THESE PRE-FETCHED PEXELS URLs (they are real, working, relevant):
LARGE images (heroes, banners, featured):
${large}

MEDIUM images (cards, products, thumbnails):
${medium}

- Use IMG_1, IMG_2 etc for large/hero images
- Use THUMB_1, THUMB_2 etc for cards and smaller images
- Assign different images to different slots — don't reuse the same URL
- For avatars/team: https://i.pravatar.cc/80?img=N (N=1 to 40)
- Add loading="lazy" to all img tags`;
        }
      } catch(e) { /* Pexels failed, fall back to picsum */ }

      // Fallback — use Unsplash Source (instant, free, topic-relevant, no API key)
      if (!imgInstructions) {
        const kwClean = kw.replace(/ /g,",");
        function uimg(query, w, h) {
          return `https://source.unsplash.com/${w}x${h}/?${encodeURIComponent(query)}`;
        }
        const heroUrl   = uimg(kw, 1400, 600);
        const hero2Url  = uimg(kw+" lifestyle", 1200, 600);
        const bannerUrl = uimg(kw+" wide", 1400, 400);
        const prod1     = uimg(kw+" product", 400, 400);
        const prod2     = uimg(kw+" detail", 400, 400);
        const prod3     = uimg(kw+" closeup", 400, 400);
        const prod4     = uimg(kw+" elegant", 400, 400);
        const prod5     = uimg(kw+" premium", 400, 400);
        const prod6     = uimg(kw+" studio", 400, 400);
        const card1     = uimg(kw+" scene", 400, 300);
        const card2     = uimg(kw+" ambient", 400, 300);
        const card3     = uimg(kw+" outdoor", 400, 300);
        const card4     = uimg(kw+" interior", 400, 300);
        const card5     = uimg(kw+" texture", 400, 300);
        const card6     = uimg(kw+" moody", 400, 300);

        imgInstructions = `
IMAGE RULES — USE ONLY THESE UNSPLASH URLs (instant loading, topic-relevant):
LARGE images (heroes, banners, featured sections):
IMG_1: ${heroUrl}
IMG_2: ${hero2Url}
IMG_3: ${bannerUrl}
IMG_4: ${card1}
IMG_5: ${card2}
IMG_6: ${card3}

MEDIUM images (product cards, thumbnails, gallery):
THUMB_1: ${prod1}
THUMB_2: ${prod2}
THUMB_3: ${prod3}
THUMB_4: ${prod4}
THUMB_5: ${prod5}
THUMB_6: ${prod6}
THUMB_7: ${card4}
THUMB_8: ${card5}
THUMB_9: ${card6}

- Use IMG_1, IMG_2 etc for large/hero areas
- Use THUMB_1 through THUMB_9 for product cards and smaller slots
- Assign DIFFERENT image to each slot — never reuse same URL
- Images load instantly
- Add loading="lazy" to all img tags
- Avatars/team: https://i.pravatar.cc/80?img=N (N=1 to 40, different per person)
- Every img tag MUST have: onerror="this.onerror=null;this.style.cssText='background:linear-gradient(135deg,#1a1a2e,#0f3460);min-height:200px;width:100%'"`;
      }

      const typeInstructions = {
        ecommerce:`
LAYOUT: Sticky navbar (logo left, nav center, search+cart right with item count badge)
HERO: Full-width parallax banner with product lifestyle image (IMG_1), bold headline, subtitle, 2 CTA buttons, subtle overlay gradient
PRODUCT GRID: 6 product cards in responsive grid (3-col desktop, 2-col tablet, 1-col mobile). Each card: product image (use THUMB_1 through THUMB_6, each different), product name matching the description, price with original crossed-out price, star rating (★★★★☆), "Add to Cart" button with hover animation. Cards have subtle shadow + lift on hover.
FILTER BAR: Category filter buttons (All + 3 relevant categories) — JS filters cards
CART SYSTEM: Cart drawer slides in from right (CSS transform translateX). Shows items, quantities, subtotal, "Checkout" CTA. Cart badge count updates in real time.
FEATURES STRIP: 4 icons + text (Free Shipping, Easy Returns, Secure Payment, 24/7 Support)
TOAST: "Added to cart ✓" — green, bottom-right, auto-dismiss
FOOTER: Newsletter signup, 4 nav columns, social icons, copyright`,

        restaurant:`
LAYOUT: Sticky transparent navbar → solid on scroll. Logo center or left.
HERO: Full-height (100vh) image (IMG_1) with dark overlay, restaurant name in large serif font, tagline, "Reserve Table" + "View Menu" CTAs
MENU SECTION: 3 tabs (Starters/Mains/Desserts or relevant categories). Each tab shows 4 dish cards: dish image (THUMB_1 through THUMB_12, different for each dish), dish name, description, price. Tab switching animated.
RESERVATION FORM: Elegant card — name, date, time picker, party size (1-10), special requests textarea — full validation, "Booking Confirmed! 🎉" success state
GALLERY: Masonry or CSS grid of 6 images (IMG_2 through IMG_7) with overlay zoom on hover
TEAM: 3 chef cards with pravatar photos, name, specialty
TESTIMONIALS: 3 review cards with stars, quote, customer name
ABOUT: Split layout — image (IMG_8) left, story text right
FOOTER: Address, hours, social links, map placeholder`,

        dashboard:`
LAYOUT: Collapsible left sidebar (icons + labels), main content area, top header with user avatar
SIDEBAR: Logo, 5-6 nav items (Overview, Analytics, Users, Orders, Reports, Settings) — clicking switches panels via JS
KPI CARDS: 4 cards with animated count-up numbers on load (use requestAnimationFrame). Revenue, Active Users, Orders, Conversion Rate. Each has icon, % change badge (green ↑ or red ↓), sparkline
CHARTS (Chart.js CDN: https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.min.js):
  - Line chart: 30-day trend with gradient fill under line
  - Bar chart: Monthly revenue with hover tooltips
  - Doughnut: Traffic sources with legend
  - Area chart: User growth (use Chart.js line with fill:true)
DATA TABLE: 8 rows, sortable columns (click header to sort JS), search/filter input, alternating row colors, status badges (Active/Pending/Inactive) with color coding`,

        analytics:`
LAYOUT: Full-width header with date range picker (7d/30d/90d/Custom buttons)
Import Chart.js: https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.min.js
KPI ROW: 5 metric cards — Sessions, Bounce Rate, Avg Duration, Conversions, Revenue. Each with big number, % change badge, inline sparkline SVG
CHARTS:
  - Large area chart: sessions over selected period (re-renders on date range click)
  - Side-by-side: bar (channel attribution) + doughnut (device breakdown)
  - Heatmap grid: 7×24 grid showing activity by hour/day (color intensity = traffic)
TABLE: Top pages table — URL, views, bounce rate, avg time. Sortable by clicking column headers. Pagination (3 pages).`,

        saas:`
LAYOUT: Transparent sticky nav → solid on scroll. Hamburger on mobile.
HERO: Centered headline (use gradient text clip), subheadline, 2 CTAs (primary + ghost), product screenshot/mockup (IMG_1) with subtle shadow + floating animation, social proof numbers below
LOGOS: "Trusted by 10,000+ teams" + 6 company logos (use SVG text logos or styled spans)
FEATURES: 3×2 grid of feature cards — large icon (emoji or SVG), bold title, 2-line description, reveal animation on scroll
HOW IT WORKS: 3 numbered steps with connector line between them, icon + title + description each
PRICING: Monthly/Annual toggle (JS, annual = 20% off). 3 cards (Starter/Pro/Enterprise). Middle card: highlighted border + "Most Popular" badge. All features listed with ✓ checkmarks.
TESTIMONIALS: 3 cards with user avatar (pravatar), quote, name, company, role
FAQ: 6 accordion items, one open at a time, smooth height animation
CTA SECTION: Full-width gradient bg, headline, email input + button
FOOTER: 4 columns, logo, tagline, social icons`,

        portfolio:`
HERO: Full viewport — animated name (letter-by-letter CSS animation), role with typing cursor effect, tagline, profile photo (https://i.pravatar.cc/200?img=47) in circle with ring animation, scroll indicator arrow bouncing
WORK GRID: 6 project cards — image (THUMB_1 through THUMB_6), project name, 3 tech tags, category label, "View Project" — clicking opens project modal with full description and live/github links
FILTER: All / [3 relevant categories] — filters with fade transition
ABOUT: 2-column split — photo + bio text, 4 stat counters (projects, clients, years, coffee ☕)
SKILLS: Animated horizontal bars — IntersectionObserver triggers fill animation when visible. 8 skills.
TIMELINE: 3 experience entries with timeline dot connector, company, role, dates, 2 bullet achievements
CONTACT: Clean form (name, email, subject, message) + direct email/phone/location display`,

        blog:`
HERO: Featured article — large image (IMG_1), category tag, headline, excerpt, author avatar+name+date, "Read Article" CTA
POST GRID: 6 cards — image (THUMB_1 through THUMB_6, each different), category badge, title, 2-line excerpt, author micro-avatar + name, date, estimated read time. Clicking card opens full article modal.
ARTICLE MODAL: Full overlay modal with scroll, full article (4-5 paragraphs), related posts, share buttons, close on X or backdrop click
CATEGORY TABS: Filter bar at top — All + 4-5 categories, smooth filter animation
SIDEBAR: Newsletter signup (email + Subscribe button, validates + shows success), popular posts list with thumbnail, tag cloud (10 tags, different font sizes by popularity)
FOOTER: Recent posts, categories, about blurb, social links`,

        landing:`
HERO: Full viewport — left text (badge, huge headline with gradient word, subtitle, 2 CTAs), right image (IMG_1) with floating animation and subtle glow
SOCIAL PROOF: Logo strip "As seen in" or "Trusted by" — 5-6 company names
FEATURES: 6 cards in 3×2 grid — icon, bold title, description. Staggered scroll reveal animation.
HOW IT WORKS: 3 steps with numbered circles, connector, icon, title, description
TESTIMONIALS: 3 cards with pravatar avatar, 5-star rating, quote, name, company
PRICING: 3 tiers (Free/Pro/Enterprise). Middle highlighted. Features checklist. "Get Started" opens modal signup form with email+password validation
FAQ: 5 accordion items
FOOTER: 4 column grid (Product/Company/Resources/Legal), newsletter, social icons`,
      };

            const specific = typeInstructions[siteType] || typeInstructions.landing;

      const styleGuides = {
        "modern-dark":"Dark bg (#0a0a0f), neon accents, glassmorphism cards, subtle dot/grid background patterns, gradient text headlines",
        "light-minimal":"Pure white bg, black typography, generous whitespace, thin borders, elegant micro-animations, sans-serif precision",
        "glassmorphism":"Semi-transparent glass panels (backdrop-filter:blur(20px) saturate(180%)), colorful gradient background, white text with shadows",
        "bold-editorial":"Magazine style, huge oversized typography, high contrast B&W + 1 accent color, editorial grid, striking section dividers",
        "brutalist":"Raw grid, monospace fonts, thick black borders, flat colors, intentionally bold unconventional layout",
        "cyberpunk":"Neon pink/cyan on near-black, scan-line effects, glitch text animations, tech grid overlays, terminal-style UI",
        "luxury":"Matte black/deep navy + gold (#c9a84c), serif display fonts, gold particle animations, premium framing and spacing",
        "playful":"Bright gradients, rounded shapes, bouncy animations, pastel colors, friendly mixed typography, emoji accents",
      };
      const styleGuide = styleGuides[style] || styleGuides["modern-dark"];

      const userPrompt=`Build a stunning, production-ready ${style} website. Make it look like it was built by a top design agency. It should look incredible.

DESCRIPTION: "${desc}"

STYLE SYSTEM — FOLLOW THIS PRECISELY: ${styleGuide}
- Pick 2 premium Google Fonts matching this style (NOT Inter/Roboto/Arial/Open Sans/Lato)
- CSS :root vars for ALL colors/radii/spacing
- All transitions: cubic-bezier(0.4, 0, 0.2, 1) 0.3s
- Cards: transform:translateY(-6px) + box-shadow boost on hover
- Buttons: scale(0.97) on :active, glow/shift on :hover

${imgInstructions}

REQUIRED SECTIONS:${specific}

ANIMATIONS & INTERACTIONS (mandatory on every site):
- HERO ENTRANCE: title fades+slides up 0ms, subtitle 150ms delay, CTAs 300ms delay (CSS animation-delay)
- NAVBAR: transparent at top, backdrop-blur + solid bg on scroll (JS scroll event listener)
- MOBILE NAV: hamburger icon, animated 3-bar to X on open, full-screen or dropdown nav
- SCROLL REVEAL: observer = new IntersectionObserver((entries)=>{ entries.forEach(e=>{ if(e.isIntersecting) e.target.classList.add('visible'); }); },{threshold:0.1}); document.querySelectorAll('.reveal').forEach(el=>observer.observe(el));
  CSS: .reveal{opacity:0;transform:translateY(32px);transition:all 0.65s cubic-bezier(.4,0,.2,1)} .reveal.visible{opacity:1;transform:none}
- FLOATING HERO IMAGE: @keyframes float{0%,100%{transform:translateY(0px)}50%{transform:translateY(-14px)}} animation:float 4s ease-in-out infinite
- TOAST: fixed bottom-right, slide up on appear, auto-dismiss 2.5s

IMAGES:
- Every <img> MUST have: src, width, height, style="object-fit:cover", loading="lazy"
- Add to every img: onerror="this.onerror=null;this.style.cssText='background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);width:100%;height:100%';this.src=''"
- USE ONLY the image URLs provided above — never invent or guess URLs

OUTPUT: Raw HTML only. <!DOCTYPE html> to </html>. No backticks. No markdown. No comments outside HTML.`;

      const maxTok = ["ecommerce","restaurant","portfolio","saas"].includes(siteType) ? 24576 : 20480;
      let result=await callAI([{role:"user",content:userPrompt}], SYS, maxTok, "web");
      let final=extractHTML(result);
      if(!final||final.length<200) {
        const fallback=`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{background:#111;color:#eee;font-family:monospace;padding:30px;white-space:pre-wrap}h2{color:#ff3c5f;margin-bottom:16px}</style></head><body><h2>⚠ Raw Gemini response (HTML extraction failed):</h2>${result.replace(/</g,"&lt;").replace(/>/g,"&gt;")}</body></html>`;
        setHtml(fallback); setHist([fallback]); setPview("code");
        setErr("HTML extraction failed — see raw response in Code tab. Click ↺ Retry."); return;
      }
      if(!isHTMLComplete(final)){
        try { final = await completeHTML(final); } catch(e) { /* use partial */ }
      }
      setHtml(final); setHist([final]);
    } catch(e){setErr(e.message);}
    finally{setLoad(false);}
  }

  async function doRefine() {
    if(!refine.trim()||refining||!html) return; setRfn(true);
    try {
      const htmlLen = html.length;
      const inputHtml = htmlLen > 12000
        ? html.slice(0,6000) + "\n\n[... middle trimmed ...]\n\n" + html.slice(-4000)
        : html;
      const result=await callAI([{role:"user",content:`Here is a website's HTML${htmlLen>12000?" (middle trimmed for brevity, apply changes globally)":""}:\n\n${inputHtml}\n\nApply ONLY these changes: "${refine}"\n\nReturn the COMPLETE updated HTML file from <!DOCTYPE html> to </html>. No markdown. No backticks.`}],SYS,24576,"refine");
      let final=extractHTML(result);
      if(!final||final.length<200){
        setErr("Refine returned empty result — try a more specific instruction."); return;
      }
      if(!isHTMLComplete(final)){
        try { final = await completeHTML(final); } catch(e) { /* use partial */ }
      }
      setHtml(final); setHist(h=>[...h,final]); setRef("");
    } catch(e){setErr(e.message);}
    finally{setRfn(false);}
  }

  return (
    <div>
      <MH icon="⬡" title="Web Builder" desc="Fully functional · Real Unsplash images · Working cart/forms/charts · Export [ For best experience use on PC ]"
        right={<RateBadge action="web"/>}/>
      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
        {TYPES.map(t=><button key={t.id} className={`btnG ${siteType===t.id?"on":""}`} onClick={()=>setST(t.id)} style={{padding:"5px 11px",fontSize:".75em"}}>{t.lb}</button>)}
      </div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:12}}>
        {STYLES.map(s=><button key={s} className={`btnG ${style===s?"on":""}`} onClick={()=>setStyle(s)} style={{padding:"4px 10px",fontSize:".71em",borderRadius:7}}>{s}</button>)}
      </div>
      <div style={{display:"flex",gap:10,marginBottom:12}}>
        <textarea value={desc} onChange={e=>setDesc(e.target.value)}
          placeholder={siteType==="ecommerce"?"e.g. Premium sneaker store with Nike, Adidas, New Balance collection..."
            :siteType==="restaurant"?"e.g. Italian fine dining restaurant with authentic pasta and wood-fired pizza..."
            :siteType==="dashboard"?"e.g. SaaS analytics dashboard for marketing teams..."
            :"Describe your website in detail..."}
          style={{flex:1,minHeight:76}}/>
        <button className="btn" onClick={build} disabled={loading} style={{alignSelf:"flex-end",minWidth:116}}>{loading?"⟳ Building...":"⬡ Build Site"}</button>
      </div>
      {errMsg&&<div style={{background:"rgba(255,60,95,.1)",border:"1px solid rgba(255,60,95,.3)",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:".83em",color:"#ff8090",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        ⚠ {errMsg}
        <button className="btnG" onClick={build} style={{fontSize:".75em",padding:"4px 10px",flexShrink:0}}>↺ Retry</button>
      </div>}
      {loading&&<div className="card" style={{textAlign:"center",padding:"40px 20px"}}>
        <div className="nx-loader" style={{marginBottom:16}}><div className="nx-x">X</div><div className="nx-label">BUILDING</div></div>
        <div style={{fontSize:".82em",color:"rgba(255,255,255,.35)"}}>Building with images matched to your content… (20–45 sec)</div>
      </div>}
      {html&&!loading&&<div className="fade">
        <div style={{display:"flex",gap:7,marginBottom:10,alignItems:"center",flexWrap:"wrap"}}>
          <div className="tabs" style={{flex:"none"}}>
            <button className={`tab ${pview==="preview"?"on":""}`} onClick={()=>setPview("preview")} style={{minWidth:96}}>👁 Preview</button>
            <button className={`tab ${pview==="code"?"on":""}`} onClick={()=>setPview("code")} style={{minWidth:80}}>&lt;/&gt; Code</button>
          </div>
          <button className="btnG" onClick={()=>{const w=window.open("","_blank");w.document.write(html);w.document.close();}} style={{fontSize:".75em"}}>⬡ Fullscreen</button>
          <button className="btnG" onClick={()=>{const a=document.createElement("a");a.href="data:text/html;charset=utf-8,"+encodeURIComponent(html);a.download="nexus-site.html";a.click();}} style={{fontSize:".75em"}}>⬇ Download</button>
          {hist.length>1&&<button className="btnG" onClick={()=>{setHtml(hist[hist.length-2]);setHist(h=>h.slice(0,-1));}} style={{fontSize:".75em"}}>↩ Undo</button>}
          <div style={{marginLeft:"auto",fontSize:".7em",color:"rgba(255,255,255,.25)",fontFamily:"monospace"}}>{(html.length/1024).toFixed(1)}kb</div>
        </div>
        {pview==="preview"&&<div style={{borderRadius:14,overflow:"hidden",border:"1px solid rgba(255,255,255,.08)",boxShadow:"0 20px 60px rgba(0,0,0,.5)"}}>
          <div style={{background:"#1a1a2a",borderBottom:"1px solid rgba(255,255,255,.07)",padding:"10px 16px",display:"flex",alignItems:"center",gap:10}}>
            <div style={{display:"flex",gap:6}}>{["#ff5f57","#ffbd2e","#28ca41"].map(c=><div key={c} style={{width:11,height:11,borderRadius:"50%",background:c}}/>)}</div>
            <div style={{flex:1,background:"rgba(255,255,255,.06)",borderRadius:6,padding:"5px 12px",fontSize:".72em",color:"rgba(255,255,255,.3)",fontFamily:"monospace"}}>nexus-live-preview</div>
            <div style={{fontSize:".66em",color:"rgba(168,255,120,.6)",fontFamily:"monospace"}}>● LIVE</div>
          </div>
          {blobUrl?<iframe ref={iframeRef} src={blobUrl} sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals" style={{width:"100%",height:640,border:"none",display:"block"}} title="preview"/>
            :<div style={{height:640,display:"flex",alignItems:"center",justifyContent:"center",background:"#0a0a1a"}}><div className="nx-loader"><div className="nx-x">X</div><div className="nx-label">PROCESSING</div></div></div>}
        </div>}
        {pview==="code"&&<div style={{borderRadius:14,overflow:"hidden",border:"1px solid var(--bdr)"}}>
          <div style={{background:"#1a1a2a",borderBottom:"1px solid rgba(255,255,255,.07)",padding:"9px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{fontSize:".68em",color:"rgba(255,255,255,.3)",fontFamily:"monospace"}}>index.html</span>
            <button className="btnG" onClick={()=>navigator.clipboard.writeText(html)} style={{fontSize:".72em",padding:"4px 10px"}}>⎘ Copy All</button>
          </div>
          <textarea value={html} onChange={e=>setHtml(e.target.value)} spellCheck={false}
            style={{width:"100%",minHeight:560,fontFamily:"'Syne Mono',monospace",fontSize:".77em",lineHeight:1.55,background:"#09091a",border:"none",borderRadius:0,color:"#a8d8a8",padding:"18px 20px",resize:"vertical"}}/>
        </div>}
        <div className="card" style={{padding:"12px 15px",marginTop:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
            <div style={{fontSize:".68em",color:"rgba(255,255,255,.26)",letterSpacing:"1px"}}>✦ REFINE</div>
            <RateBadge action="refine"/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <input type="text" value={refine} onChange={e=>setRef(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")doRefine();}}
              placeholder='e.g. "Add working checkout", "Make cart sidebar slide in", "Add customer reviews with photos"'/>
            <button className="btn" onClick={doRefine} disabled={refining} style={{minWidth:90}}>{refining?"...":"✦ Refine"}</button>
          </div>
        </div>
      </div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   STUDY AI — Tutor + Voice + File Upload + Quiz + Interview + Resume
══════════════════════════════════════════════════════════ */
// ── Auto-generate Chart.js configs — smart type selection ──
function buildChartsFromData(rows, headers) {
  const COLORS=["#ff3c5f","#9b1dff","#00d4ff","#a8ff78","#ffbd2e","#ff8c42","#4ecdc4","#f7b2bd"];

  // Classify columns by type
  const numCols=headers.filter(h=>{
    const vals=rows.map(r=>r[h]).filter(v=>v&&v!=="");
    const nums=vals.map(v=>parseFloat(v)).filter(v=>!isNaN(v));
    return nums.length/vals.length>0.7;
  });
  const catCols=headers.filter(h=>!numCols.includes(h));
  const dateCols=catCols.filter(h=>rows.slice(0,10).some(r=>r[h]&&/\d{4}[-\/]\d{2}|\d{2}[-\/]\d{2}[-\/]\d{4}/.test(r[h])));

  // Keyword hints for smart chart type selection
  const PIE_KEYS   = /share|percent|%|ratio|portion|distribution|population|segment|breakdown|proportion|category/i;
  const LINE_KEYS  = /date|month|year|quarter|week|day|time|period|trend|growth|revenue|sales|profit/i;
  const BAR_KEYS   = /count|total|sum|amount|score|rank|performance|rating|quantity|units|volume/i;
  const GROUP_KEYS = /region|team|department|product|category|type|group|branch|zone|country|city/i;

  function detectChartType(catCol, numCol) {
    const both = (catCol + " " + numCol).toLowerCase();
    // Date column → always line
    if(dateCols.includes(catCol)) return "line";
    // Keyword match
    if(PIE_KEYS.test(both)) return "doughnut";
    if(LINE_KEYS.test(numCol)) return "line";
    if(BAR_KEYS.test(numCol) || GROUP_KEYS.test(catCol)) return "bar";
    // Cardinality: few unique values → pie, many → bar
    const unique=new Set(rows.map(r=>r[catCol])).size;
    if(unique<=6) return "doughnut";
    return "bar";
  }

  // Helper: group and sum
  function groupSum(catCol, numCol, maxGroups=8) {
    const map={};
    rows.forEach(r=>{
      const k=r[catCol]||"Unknown";
      map[k]=(map[k]||0)+parseFloat(r[numCol]||0);
    });
    const entries=Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,maxGroups);
    // For line charts, sort by label (date order)
    if(dateCols.length>0) entries.sort((a,b)=>a[0].localeCompare(b[0]));
    return {
      labels:entries.map(([k])=>k),
      data:entries.map(([,v])=>Math.round(v*100)/100)
    };
  }

  const charts=[];

  // ── Chart 1: Primary metric — best cat vs best numeric ──
  if(catCols.length>0 && numCols.length>0) {
    // Prefer date col if exists, else first meaningful cat
    const cat = dateCols[0] || catCols.find(h=>GROUP_KEYS.test(h)) || catCols[0];
    const num = numCols.find(h=>LINE_KEYS.test(h)||BAR_KEYS.test(h)) || numCols[numCols.length-1];
    const type = detectChartType(cat, num);
    const {labels,data} = groupSum(cat, num, type==="doughnut"?7:10);
    if(labels.length>1) charts.push({
      title: `${num.replace(/_/g," ")} by ${cat.replace(/_/g," ")}`,
      type,
      labels,
      datasets:[{
        label: num.replace(/_/g," "),
        data,
        backgroundColor: type==="line" ? "rgba(255,60,95,.15)" : COLORS.slice(0,labels.length),
        borderColor: type==="line" ? "#ff3c5f" : undefined,
        borderWidth: type==="line" ? 2 : undefined,
        fill: type==="line" ? true : undefined,
      }]
    });
  }

  // ── Chart 2: Secondary dimension ──
  if(catCols.length>1 && numCols.length>0) {
    // Use a different cat col than chart 1
    const usedCat = charts[0] ? (dateCols[0]||catCols[0]) : null;
    const cat = catCols.find(h=>h!==usedCat && GROUP_KEYS.test(h)) || catCols.find(h=>h!==usedCat) || catCols[1];
    const num = numCols.find(h=>BAR_KEYS.test(h)) || numCols[0];
    const type = detectChartType(cat, num);
    const {labels,data} = groupSum(cat, num, type==="doughnut"?7:10);
    if(labels.length>1) charts.push({
      title: `${num.replace(/_/g," ")} by ${cat.replace(/_/g," ")}`,
      type,
      labels,
      datasets:[{
        label: num.replace(/_/g," "),
        data,
        backgroundColor: type==="line" ? "rgba(155,29,255,.15)" : COLORS.slice(0,labels.length),
        borderColor: type==="line" ? "#9b1dff" : undefined,
        borderWidth: type==="line" ? 2 : undefined,
        fill: type==="line" ? true : undefined,
      }]
    });
  }

  // ── Chart 3: If 2+ numeric cols, show comparison bar ──
  if(numCols.length>=2 && catCols.length>0) {
    const cat = catCols.find(h=>GROUP_KEYS.test(h)&&!dateCols.includes(h)) || catCols[0];
    const num1 = numCols[0]; const num2 = numCols[1];
    const g1=groupSum(cat,num1,7); const g2=groupSum(cat,num2,7);
    if(g1.labels.length>1) charts.push({
      title: `${num1.replace(/_/g," ")} vs ${num2.replace(/_/g," ")}`,
      type: "bar",
      labels: g1.labels,
      datasets:[
        {label:num1.replace(/_/g," "),data:g1.data,backgroundColor:"rgba(255,60,95,.7)"},
        {label:num2.replace(/_/g," "),data:g2.data,backgroundColor:"rgba(155,29,255,.7)"},
      ]
    });
  } else if(catCols.length>0 && numCols.length>0) {
    // Fallback: distribution doughnut
    const cat = catCols.find(h=>!dateCols.includes(h)) || catCols[0];
    const num = numCols[numCols.length-1];
    const {labels,data}=groupSum(cat,num,7);
    if(labels.length>1 && !charts.find(c=>c.type==="doughnut")) charts.push({
      title:`${num.replace(/_/g," ")} distribution`,
      type:"doughnut",
      labels,
      datasets:[{label:num.replace(/_/g," "),data,backgroundColor:COLORS.slice(0,labels.length)}]
    });
  }

  // ── Fallback: pure count chart if no numeric cols ──
  if(charts.length===0 && catCols.length>0) {
    const cat=catCols[0];
    const map={};
    rows.forEach(r=>{const k=r[cat]||"Unknown";map[k]=(map[k]||0)+1;});
    const entries=Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,8);
    const unique=entries.length;
    charts.push({
      title:`Count by ${cat.replace(/_/g," ")}`,
      type: unique<=6?"doughnut":"bar",
      labels:entries.map(([k])=>k),
      datasets:[{label:"Count",data:entries.map(([,v])=>v),backgroundColor:COLORS.slice(0,entries.length)}]
    });
  }

  return charts.slice(0,3);
}

/* ══════════════════════════════════════════════════════════
   DATA LAB — Clean · Analyse · Report
══════════════════════════════════════════════════════════ */
function DataModule() {
  const [file,setFile]=useState(null);
  const [report,setReport]=useState("");
  const [charts,setCharts]=useState([]);
  const [cleanLog,setCleanLog]=useState([]);
  const [loading,setLoad]=useState(false);
  const [stage,setStage]=useState("");
  const [err,setErr]=useState("");
  const [drag,setDrag]=useState(false);
  const chartsRef=useRef({});

  // ── CSV parser ─────────────────────────────────────────
  function parseCSV(text) {
    const lines=text.trim().split("\n").filter(l=>l.trim());
    const headers=lines[0].split(",").map(h=>h.trim().replace(/^"|"$/g,""));
    return {headers, rows:lines.slice(1).map(l=>{
      const vals=l.split(",").map(v=>v.trim().replace(/^"|"$/g,""));
      const obj={};
      headers.forEach((h,i)=>obj[h]=vals[i]||"");
      return obj;
    })};
  }

  function parseJSON(text) {
    try{const d=JSON.parse(text);return Array.isArray(d)?d:[d];}catch{return null;}
  }

  // ── Data Cleaner ───────────────────────────────────────
  function cleanData(rows, headers) {
    const log=[];
    let clean=[...rows];

    // 1. Remove fully empty rows
    const beforeEmpty=clean.length;
    clean=clean.filter(r=>headers.some(h=>r[h]&&r[h].trim()!==""));
    if(clean.length<beforeEmpty) log.push(`🗑 Removed ${beforeEmpty-clean.length} empty rows`);

    // 2. Remove duplicate rows
    const seen=new Set();
    const beforeDupe=clean.length;
    clean=clean.filter(r=>{const k=JSON.stringify(r);if(seen.has(k))return false;seen.add(k);return true;});
    if(clean.length<beforeDupe) log.push(`🗑 Removed ${beforeDupe-clean.length} duplicate rows`);

    // 3. Detect column types & fix values
    headers.forEach(h=>{
      const vals=clean.map(r=>r[h]).filter(v=>v&&v.trim()!=="");
      if(!vals.length) return;

      // Strip currency/percent symbols → try numeric
      const numeric=vals.map(v=>parseFloat(v.replace(/[$,%€£]/g,"").replace(/,/g,""))).filter(v=>!isNaN(v));
      const isNumeric=numeric.length/vals.length>0.7;

      // Fix currency/comma formatted numbers
      if(isNumeric) {
        let fixed=0;
        clean=clean.map(r=>{
          const raw=r[h];
          if(!raw||raw.trim()==="") return r;
          const n=parseFloat(raw.replace(/[$,%€£]/g,"").replace(/,/g,""));
          if(!isNaN(n)&&raw!==String(n)){fixed++;return{...r,[h]:String(n)};}
          return r;
        });
        if(fixed>0) log.push(`🔧 Cleaned ${fixed} values in "${h}" (removed currency/comma formatting)`);
      }

      // Fix missing values
      const missing=clean.filter(r=>!r[h]||r[h].trim()==="").length;
      if(missing>0) {
        if(isNumeric) {
          const avg=(numeric.reduce((a,b)=>a+b,0)/numeric.length).toFixed(2);
          clean=clean.map(r=>(!r[h]||r[h].trim()==="")?{...r,[h]:String(avg)}:r);
          log.push(`🔧 Filled ${missing} missing values in "${h}" with column average (${avg})`);
        } else {
          clean=clean.map(r=>(!r[h]||r[h].trim()==="")?{...r,[h]:"Unknown"}:r);
          log.push(`🔧 Filled ${missing} missing values in "${h}" with "Unknown"`);
        }
      }

      // Standardize date formats
      const datelike=vals.filter(v=>/\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(v));
      if(datelike.length/vals.length>0.5) {
        let fixed=0;
        clean=clean.map(r=>{
          const d=new Date(r[h]);
          if(!isNaN(d.getTime())&&r[h]!==d.toISOString().slice(0,10)){fixed++;return{...r,[h]:d.toISOString().slice(0,10)};}
          return r;
        });
        if(fixed>0) log.push(`📅 Standardized ${fixed} date values in "${h}" to YYYY-MM-DD`);
      }
    });

    // 4. Standardize column names
    const renamedCols=[];
    const newHeaders=headers.map(h=>{
      const clean=h.trim().replace(/\s+/g,"_").replace(/[^a-zA-Z0-9_]/g,"");
      if(clean!==h) renamedCols.push(`"${h}"→"${clean}"`);
      return clean;
    });
    if(renamedCols.length>0) {
      clean=clean.map(r=>{
        const newR={};
        headers.forEach((h,i)=>newR[newHeaders[i]]=r[h]);
        return newR;
      });
      log.push(`📝 Standardized ${renamedCols.length} column names: ${renamedCols.slice(0,3).join(", ")}${renamedCols.length>3?"…":""}`);
    }

    if(log.length===0) log.push("✅ Data was already clean — no issues found");
    return {clean, headers:newHeaders, log};
  }

  async function processFile(f) {
    setFile(f); setErr(""); setReport(""); setCharts([]); setCleanLog([]); setLoad(true);
    try {
      const text=await f.text();
      let rawRows=[], rawHeaders=[];
      if(f.name.endsWith(".json")){
        rawRows=parseJSON(text)||[];
        rawHeaders=rawRows.length?Object.keys(rawRows[0]):[];
      } else {
        const parsed=parseCSV(text);
        rawRows=parsed.rows; rawHeaders=parsed.headers;
      }
      if(!rawRows.length) throw new Error("Could not parse file — please use CSV or JSON");

      // ── Step 1: Clean ──────────────────────────────────
      setStage("cleaning");
      const {clean:cleanRows, headers:cleanHeaders, log}=cleanData(rawRows,rawHeaders);
      setCleanLog(log);

      // ── Step 2: Detect data type ───────────────────────
      setStage("detecting");
      const sample=JSON.stringify(cleanRows.slice(0,30),null,1);
      const detection=await callAI([{role:"user",content:
        `Dataset columns: ${cleanHeaders.join(", ")}\nSample:\n${sample}\n\nIn ONE sentence, what type of business data is this?`
      }],"One sentence only.",128);

      // ── Step 3: Full analysis + report ────────────────
      setStage("analyzing");
      const fullSample=JSON.stringify(cleanRows.slice(0,80),null,1);
      const reportRaw=await callAI([{role:"user",content:
        `You are a senior business analyst. Analyze this ${detection} and write a professional business report.

Dataset: ${cleanRows.length} rows (after cleaning), columns: ${cleanHeaders.join(", ")}
Data sample:
${fullSample}

Write the report in this EXACT structure:

# [Specific Report Title Based on Data]

## Executive Summary
3-4 sentences with the most critical findings for the business owner. Use actual numbers.

## Data Overview
Total records, key columns, date range if available, data completeness.

## Key Findings
5-7 specific numbered insights with actual numbers and percentages from the data.

## Performance Analysis
Deep dive into the most important metrics. Identify top performers, bottom performers, comparisons between segments.

## Trends & Patterns
Trends over time, correlations, seasonality, anomalies detected.

## Recommendations
5 specific, actionable recommendations the business owner can act on immediately.

## Conclusion
2-3 sentences summarizing the overall picture and next steps.

Be specific. Use real numbers from the data. Avoid generic statements.`
      }],"You are a senior business analyst. Be specific and data-driven.",8192,"data");

      // ── Step 4: Charts — pure JS, no API call needed ────
      setStage("charting");
      setCharts(buildChartsFromData(cleanRows, cleanHeaders));
      setReport(reportRaw);
      setStage("done");
    } catch(e){setErr(e.message);}
    finally{setLoad(false);}
  }

  // Self-contained chart component — renders after canvas is in DOM
  function ChartCanvas({cfg, idx}) {
    const ref=useRef(null);
    const cfgStr = JSON.stringify(cfg); // stable dependency
    useEffect(()=>{
      if(!ref.current) return;
      const doRender=()=>{
        if(chartsRef.current[idx]) chartsRef.current[idx].destroy();
        chartsRef.current[idx]=new Chart(ref.current,{
          type:cfg.type,
          data:{labels:cfg.labels,datasets:cfg.datasets.map(ds=>({
            ...ds,
            borderColor:cfg.type==="line"?"#ff3c5f":undefined,
            borderWidth:cfg.type==="line"?2:undefined,
            fill:cfg.type==="line"?false:undefined,
          }))},
          options:{responsive:true,plugins:{legend:{labels:{color:document.documentElement.getAttribute("data-theme")==="light"?"#333":"#bac2de"}},title:{display:true,text:cfg.title,color:document.documentElement.getAttribute("data-theme")==="light"?"#1a1a2e":"#e8e8f2",font:{size:13}}},
            scales:cfg.type==="pie"||cfg.type==="doughnut"?{}:{x:{ticks:{color:"#8888aa"},grid:{color:"rgba(255,255,255,.05)"}},y:{ticks:{color:"#8888aa"},grid:{color:"rgba(255,255,255,.06)"}}}}
        });
      };
      if(ref.current) doRender();
      return()=>{ if(chartsRef.current[idx]){chartsRef.current[idx].destroy();chartsRef.current[idx]=null;} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[cfgStr, idx]);
    return <canvas ref={ref} style={{maxHeight:220}}/>;
  }

  const stageLabel={cleaning:"🧹 Cleaning data…",detecting:"🔍 Detecting data type…",analyzing:"📊 Analyzing dataset…",charting:"📈 Generating charts…"};

  return (
    <div>
      <MH icon="◎" title="Analysis Lab" desc="Clean · Analyse · Report — drop any raw dataset" right={<RateBadge action="data"/>}/>

      {!loading&&!report&&<div className={`upload-zone ${drag?"drag":""}`}
        style={{padding:"44px 20px",marginBottom:16,textAlign:"center"}}
        onDragOver={e=>{e.preventDefault();setDrag(true);}}
        onDragLeave={()=>setDrag(false)}
        onDrop={e=>{e.preventDefault();setDrag(false);const f=e.dataTransfer.files[0];if(f)processFile(f);}}
        onClick={()=>document.getElementById("data-file-input").click()}>
        <div style={{fontSize:"2.2em",marginBottom:10}}>◎</div>
        <div style={{fontWeight:700,marginBottom:6,color:"var(--txt)"}}>Drop your raw dataset here</div>
        <div style={{fontSize:".78em",color:"rgba(255,255,255,.3)",marginBottom:8}}>CSV · JSON &nbsp;·&nbsp; Messy data welcome — we'll clean it</div>
        <div style={{display:"flex",justifyContent:"center",gap:8,flexWrap:"wrap"}}>
          {["Sales data","HR performance","Marketing","Finance","Any CSV"].map(t=>(
            <span key={t} style={{fontSize:".68em",background:"rgba(255,60,95,.08)",border:"1px solid rgba(255,60,95,.2)",borderRadius:20,padding:"3px 10px",color:"rgba(255,60,95,.7)"}}>{t}</span>
          ))}
        </div>
        <input id="data-file-input" type="file" accept=".csv,.json" style={{display:"none"}}
          onChange={e=>e.target.files[0]&&processFile(e.target.files[0])}/>
      </div>}

      {loading&&<div className="card" style={{textAlign:"center",padding:"50px 20px"}}>
        <div className="nx-loader" style={{marginBottom:12}}><div className="nx-x">X</div></div>
        <div style={{fontSize:".85em",color:"rgba(255,255,255,.5)",marginTop:8}}>{stageLabel[stage]||"Processing…"}</div>
      </div>}

      {err&&<div style={{background:"rgba(255,60,95,.1)",border:"1px solid rgba(255,60,95,.3)",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:".83em",color:"#ff8090"}}>⚠ {err}</div>}

      {report&&!loading&&<div className="fade">
        <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{fontSize:".72em",color:"rgba(255,255,255,.3)",flex:1}}>◎ {file?.name}</div>
          <button className="btnG" style={{fontSize:".75em"}} onClick={()=>{setReport("");setCharts([]);setFile(null);setCleanLog([]);setStage("");}}>↺ New File</button>
          <button className="btn" style={{fontSize:".78em"}} onClick={()=>exportPDF(file?.name?.replace(/\.[^.]+$/,"")||"Data Report",report)}>⬇ Export PDF</button>
        </div>

        {/* Cleaning summary */}
        {cleanLog.length>0&&<div className="card" style={{padding:"14px 18px",marginBottom:14,borderColor:"rgba(168,255,120,.15)"}}>
          <div className="data-clean-summary" style={{fontSize:".68em",letterSpacing:"1.5px",color:"#a8ff78",marginBottom:8,fontWeight:700}}>🧹 DATA CLEANING SUMMARY</div>
          {cleanLog.map((l,i)=><div key={i} className="data-clean-log" style={{fontSize:".78em",marginBottom:3}}>{l}</div>)}
        </div>}

        {/* Charts */}
        {charts.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:14,marginBottom:16}}>
          {charts.map((cfg,i)=>(
            <div key={i} className="card" style={{padding:"14px 16px"}}>
              <ChartCanvas cfg={cfg} idx={i}/>
            </div>
          ))}
        </div>}

        {/* Report */}
        <div className="card data-report" style={{padding:"24px 28px",lineHeight:1.9,fontSize:".86em",color:"var(--txt2)"}}
          dangerouslySetInnerHTML={{__html:md(report)}}/>
      </div>}
    </div>
  );
}

function StudyModule() {
  const [tab,setTab]=useState("home");
  const [chatHist,setCH]=useState([]);
  const [chatIn,setCIn]=useState(""); const [chatLoad,setCL]=useState(false);
  const [topic,setTopic]=useState(""); const [topicIn,setTI]=useState("");
  const [qTopic,setQT]=useState(""); const [qDiff,setQD]=useState("intermediate");
  const [qData,setQD2]=useState(null); const [qLoad,setQL]=useState(false);
  const [sel,setSel]=useState(null); const [qExp,setQE]=useState(""); const [score,setScore]=useState({c:0,t:0});
  const [rTab,setRT]=useState("templates"); const [rTpl,setRTpl]=useState(RESUME_TEMPLATES[0]);
  const [rForm,setRF]=useState({name:"",contact:"",summary:"",experience:"",education:"",skills:"",extra:""});
  const [rLoad,setRL]=useState(false); const [rHTML,setRHTML]=useState("");
  const chatEnd=useRef();
  const saveTimer=useRef(null);
  useEffect(()=>chatEnd.current?.scrollIntoView({behavior:"smooth"}),[chatHist]);
  // Ensure we also auto-scroll when switching back to the chat tab (e.g. after starting a topic or opening a PDF)
  useEffect(()=>{ if(tab==="chat") chatEnd.current?.scrollIntoView({behavior:"smooth"}); },[tab]);

  // ── Load study data from DB on mount ──
  useEffect(()=>{
    const token=_sessionToken;
    if(!token) {
      // fallback to localStorage if no session
      try{const h=JSON.parse(localStorage.getItem("nx_ch")||"[]");if(h.length)setCH(h);}catch{}
      return;
    }
    fetch("/api/study/load",{headers:{"x-session-token":token}})
      .then(r=>r.json())
      .then(d=>{
        if(d.chatHist?.length) {
          setCH(d.chatHist);
        } else {
          // Per-account fallback in localStorage so different accounts don't share history
          try{
            const h=JSON.parse(localStorage.getItem(`nx_ch_${token}`)||"[]");
            if(h.length)setCH(h);
          }catch{}
        }
        if(d.score) setScore(d.score);
        if(d.topic) setTopic(d.topic);
      }).catch(()=>{
        // If DB load fails, use per-account localStorage key so study stats differ per user
        try{
          const h=JSON.parse(localStorage.getItem(`nx_ch_${token}`)||"[]");
          if(h.length)setCH(h);
        }catch{}
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // ── Save study data to DB (debounced 2s) ──
  function syncToDB(hist, sc, tp) {
    if(!_sessionToken) return;
    clearTimeout(saveTimer.current);
    saveTimer.current=setTimeout(()=>{
      fetch("/api/study/save",{
        method:"POST",
        headers:{"Content-Type":"application/json","x-session-token":_sessionToken},
        body:JSON.stringify({chatHist:hist, score:sc, topic:tp})
      }).catch(()=>{});
    },2000);
  }

  function saveCH(m){
    setCH(m);
    // Persist last 80 messages; namespace by session token so each account has its own history
    try{
      const key=_sessionToken?`nx_ch_${_sessionToken}`:"nx_ch";
      localStorage.setItem(key,JSON.stringify(m.slice(-80)));
    }catch{}
    syncToDB(m, score, topic);
  }

  // Voice for tutor
  const {listening,toggle:toggleVoice}=useSpeechInput(t=>setCIn(p=>p+(p?" ":"")+t));

  // File upload
  const [fileLoad,setFLoad]=useState(false); const [fileProg,setFProg]=useState("");
  const fileRef=useRef(null);

  async function handleFile(file) {
    if(!file) return;
    setFLoad(true); setFProg(`Reading ${file.name}…`);
    try {
      let extractedText="";
      const ext=file.name.split(".").pop().toLowerCase();

      if(ext==="txt"||file.type==="text/plain") {
        // Plain text — read directly
        extractedText=await file.text();
        setFProg("Analyzing text…");

      } else if(ext==="pdf"||file.type==="application/pdf") {
        // PDF — send as base64 to Gemini vision
        setFProg("Reading PDF…");
        const b64=await fileToBase64(file);
        setFProg("Extracting PDF content…");
        extractedText=await callAIMulti(
          [{type:"document",source:{type:"base64",media_type:"application/pdf",data:b64}},
           {type:"text",text:"Extract ALL text from this PDF document. Return the complete text content, preserving structure and layout as much as possible. Include all headings, paragraphs, lists, and tables."}],
          "Extract document text faithfully and completely.",8192,"tutor");

      } else if(ext==="docx"||file.type==="application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
        // DOCX — use mammoth.js loaded from CDN
        setFProg("Reading Word document…");
        const mammoth=await loadMammoth();
        const arrayBuffer=await file.arrayBuffer();
        const result=await mammoth.extractRawText({arrayBuffer});
        extractedText=result.value;
        setFProg("Analyzing Word document…");

      } else if(file.type.startsWith("image/")||["png","jpg","jpeg","webp"].includes(ext)) {
        // Image — send to Gemini vision for OCR
        setFProg("Analyzing image…");
        const b64=await fileToBase64(file);
        extractedText=await callAIMulti(
          [{type:"image",source:{type:"base64",media_type:file.type||"image/jpeg",data:b64}},
           {type:"text",text:"Transcribe ALL text visible in this image. If it contains handwritten notes, typed text, diagrams with labels, or any other text content — extract everything. Preserve the structure and formatting as much as possible."}],
          "Extract and transcribe all text from images accurately.",4096,"tutor");
      } else {
        throw new Error(`Unsupported file type: .${ext}`);
      }

      if(!extractedText||extractedText.trim().length<20) {
        throw new Error("Could not extract readable text from this file.");
      }

      // Generate structured study material
      setFProg("Generating study materials…");
      const studyContent=await callAI(
        [{role:"user",content:`Here is content from the file "${file.name}":\n\n${extractedText.slice(0,14000)}\n\nCreate comprehensive study materials:\n\n## 📋 Summary\n[5-7 key points]\n\n## ❓ 10 Study Questions & Answers\n[Q1: ...\\nA1: ...\\nQ2: ...etc]\n\n## 🔑 Key Concepts\n[list the most important terms and ideas]\n\n## 📚 Study Plan\n[3-step plan to master this material]`}],
        "You are an expert tutor. Create detailed, useful study materials that will genuinely help the student learn.",4096,"tutor");

      const newMsg={role:"assistant",content:`📄 **Analyzed: "${file.name}"**\n\n${studyContent}`,ts:Date.now()};
      saveCH([...chatHist,newMsg]);
      setTab("chat");
    } catch(e){
      const errMsg={role:"assistant",content:`⚠ File error: ${e.message}`,ts:Date.now()};
      saveCH([...chatHist,errMsg]); setTab("chat");
    } finally{setFLoad(false); setFProg("");}
  }

  // Load mammoth.js dynamically for DOCX
  function loadMammoth() {
    return new Promise((resolve,reject)=>{
      if(window.mammoth){resolve(window.mammoth);return;}
      const s=document.createElement("script");
      s.src="https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
      s.onload=()=>resolve(window.mammoth); s.onerror=()=>reject(new Error("Could not load DOCX reader"));
      document.head.appendChild(s);
    });
  }

  async function sendChat() {
    if(!chatIn.trim()||chatLoad) return;
    const um={role:"user",content:chatIn,ts:Date.now()};
    const h=[...chatHist,um]; saveCH(h); setCIn(""); setCL(true);
    try {
      const reply=await callAI(h.map(m=>({role:m.role,content:m.content})),
        `You are an expert patient tutor. Topic: "${topic||"general"}". Teach clearly with analogies, examples, step-by-step. Use **bold** for key terms.`,4096,"tutor");
      saveCH([...h,{role:"assistant",content:reply,ts:Date.now()}]);
    } finally{setCL(false);}
  }

  async function nextQ(retry=0) {
    if(!qTopic.trim()||qLoad) return;
    setQL(true); setSel(null); setQE(""); setQD2(null);
    try {
      const raw=await callAI(
        [{role:"user",content:`Generate a ${qDiff} multiple-choice question about: "${qTopic}".
Output ONLY a JSON object. No text before or after. No markdown. No backticks.
Format: {"question":"...","options":["A) ...","B) ...","C) ...","D) ..."],"correct":0,"explanation":"..."}`}],
        "You output only raw JSON. No markdown. No explanation. No backticks.",1024,"quiz");
      // Try to extract JSON even if model adds extra text
      const cleaned=raw.replace(/```json?/g,"").replace(/```/g,"").trim();
      let parsed=null;
      try{ parsed=JSON.parse(cleaned); }
      catch{
        const m=cleaned.match(/\{[\s\S]*?\}/);
        if(m) try{ parsed=JSON.parse(m[0]); }catch{}
      }
      // Validate structure
      if(parsed?.question && Array.isArray(parsed?.options) && parsed.options.length>=2){
        setQD2(parsed);
      } else {
        // Auto-retry up to 2 times
        if(retry<2){ setQL(false); setTimeout(()=>nextQ(retry+1),600); return; }
        setQD2({question:"⚠ Could not generate question — try a different topic or click Next again.",options:["Retry →"],correct:0,explanation:"The AI returned an unexpected response. Try rephrasing your topic."});
      }
    } catch(e){
      if(retry<2){ setQL(false); setTimeout(()=>nextQ(retry+1),800); return; }
      setQD2({question:`⚠ ${e.message} — click Next to retry.`,options:["Retry →"],correct:0,explanation:"Something went wrong. Click Next Question to try again."});
    }
    finally{setQL(false);}
  }
  function answer(i){
    if(sel!==null||!qData)return;
    setSel(i);setQE(qData.explanation);
    const newScore={c:score.c+(i===qData.correct?1:0),t:score.t+1};
    setScore(newScore);
    syncToDB(chatHist, newScore, topic);
  }

  async function genResume() {
    if(!rForm.name||rLoad)return; setRL(true);
    try {
      const raw=await callAI([{role:"user",content:`ATS-optimize this resume. Return ONLY JSON (no fences):\n{"name":"","contact":"","summary":"","experience":"","education":"","skills":"","extra":""}\n\nOriginal:\n${JSON.stringify(rForm)}`}],
        "Professional resume writer. Return only JSON.",2048,"tutor");
      const parsed=JSON.parse(raw.replace(/```json?\n?/g,"").replace(/```/g,"").trim());
      setRHTML(buildResumeHTML(rTpl,parsed)); setRT("preview");
    } catch{setRHTML(buildResumeHTML(rTpl,rForm)); setRT("preview");}
    finally{setRL(false);}
  }

  const recentTopics=[...new Set(chatHist.filter(m=>m.role==="user").map(m=>m.content.slice(0,28)))];

  return (
    <div>
      <MH icon="◉" title="Mentor" desc="Tutor · Voice · PDF/DOCX/Image Upload · Quiz · Mock Interview · Resume"/>
      <div className="tabs" style={{marginBottom:20}}>
        {[["home","◈ Home"],["chat","Tutor"],["quiz","Quiz"],["interview","Interview"],["resume","Resume"]].map(([id,lb])=>(
          <button key={id} className={`tab ${tab===id?"on":""}`} onClick={()=>setTab(id)}>{lb}</button>
        ))}
      </div>

      {/* HOME */}
      {tab==="home"&&<div className="fade">
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:11,marginBottom:18}}>
          {[{lb:"Messages",v:chatHist.length,ic:"◈"},{lb:"Topics",v:recentTopics.length,ic:"⬡"},{lb:"Accuracy",v:score.t>0?Math.round(score.c/score.t*100)+"%":"—",ic:"◉"},{lb:"Correct",v:`${score.c}/${score.t}`,ic:"✦"}].map(s=>(
            <div key={s.lb} className="card" style={{textAlign:"center",padding:"16px 10px"}}>
              <div style={{fontFamily:"monospace",fontSize:"1.2em",color:"var(--R)",marginBottom:4}}>{s.ic}</div>
              <div className="stat-val" style={{fontSize:"1.7em",fontWeight:800,color:"var(--txt)",fontFamily:"'Syne',sans-serif"}}>{s.v}</div>
              <div style={{fontSize:".65em",color:"var(--txt2)",marginTop:3,letterSpacing:"1px"}}>{s.lb.toUpperCase()}</div>
            </div>
          ))}
        </div>
        {/* Usage meters */}
        <div className="card" style={{marginBottom:14}}>
          <div style={{fontSize:".7em",color:"var(--txt2)",letterSpacing:"1.5px",marginBottom:12}}>TODAY'S USAGE</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(118px,1fr))",gap:8}}>
            {Object.entries(_usageState.limits||PLAN_LIMITS.free).map(([k,lim])=>{const used=getUse(k);const pct=lim>0?used/lim:0;const col=pct<0.5?"var(--G)":pct<0.8?"#ffd166":"var(--R)";return(
              <div key={k} style={{background:"rgba(255,255,255,.03)",borderRadius:9,padding:"9px 12px"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <span style={{fontSize:".7em",color:"var(--txt2)",textTransform:"capitalize"}}>{k}</span>
                  <span style={{fontSize:".7em",color:col,fontWeight:700}}>{used}/{lim}</span>
                </div>
                <div style={{height:3,background:"var(--bdr)",borderRadius:3}}>
                  <div style={{height:"100%",width:`${Math.min(pct*100,100)}%`,background:col,borderRadius:3,transition:"width .3s"}}/>
                </div>
              </div>
            );})}
          </div>
        </div>
        <div className="card">
          <div style={{fontSize:".7em",color:"var(--txt2)",letterSpacing:"1.5px",marginBottom:9}}>START STUDY SESSION</div>
          <div style={{display:"flex",gap:8}}>
            <input type="text" value={topicIn} onChange={e=>setTI(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&topicIn.trim()){setTopic(topicIn);setTI("");setTab("chat");}}}
              placeholder="e.g. Quantum Computing, React Hooks, French Revolution…"/>
            <button className="btn" onClick={()=>{if(topicIn.trim()){setTopic(topicIn);setTI("");setTab("chat");}}} style={{minWidth:116}}>Start →</button>
          </div>
        </div>
      </div>}

      {/* TUTOR CHAT + VOICE + FILE UPLOAD */}
      {tab==="chat"&&<div className="fade">
        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:11,flexWrap:"wrap"}}>
          {topic&&<><span className="tag">TOPIC</span><span style={{fontSize:".83em",color:"rgba(255,255,255,.54)"}}>{topic}</span></>}
          <button className="btnG" onClick={()=>setTab("home")} style={{marginLeft:"auto",fontSize:".7em",padding:"4px 9px"}}>Change Topic</button>
          <RateBadge action="tutor"/>
        </div>

        {/* File Upload Zone */}
        <div className="card" style={{marginBottom:12,padding:"12px 14px"}}>
          <div style={{fontSize:".68em",color:"rgba(255,255,255,.3)",letterSpacing:"1.5px",marginBottom:8}}>📎 UPLOAD STUDY MATERIAL</div>
          <input ref={fileRef} type="file" accept=".pdf,.txt,.docx,.png,.jpg,.jpeg,.webp" onChange={e=>e.target.files?.[0]&&handleFile(e.target.files[0])} style={{display:"none"}}/>
          {fileLoad?(
            <div style={{padding:"16px",textAlign:"center"}}>
              <div className="nx-loader" style={{marginBottom:10}}><div className="nx-x">X</div><div className="nx-label">ANALYZING</div></div>
              <div style={{fontSize:".78em",color:"rgba(255,255,255,.4)"}}>{fileProg}</div>
            </div>
          ):(
            <div className="upload-zone" onClick={()=>fileRef.current?.click()}
              onDragOver={e=>{e.preventDefault();e.currentTarget.classList.add("drag")}}
              onDragLeave={e=>e.currentTarget.classList.remove("drag")}
              onDrop={e=>{e.preventDefault();e.currentTarget.classList.remove("drag");e.dataTransfer.files[0]&&handleFile(e.dataTransfer.files[0]);}}>
              <div style={{fontSize:"1.8em",marginBottom:6}}>📄</div>
              <div style={{fontSize:".82em",color:"var(--txt2)",marginBottom:3,fontWeight:600}}>Click or drag & drop a file</div>
              <div style={{fontSize:".72em",color:"rgba(255,255,255,.28)"}}>PDF · DOCX · TXT · PNG / JPG (handwritten notes) — AI creates summary + Q&A</div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div style={{display:"flex",flexDirection:"column",gap:11,maxHeight:"44vh",overflowY:"auto",marginBottom:12,paddingRight:2}}>
          {chatHist.length===0&&<div style={{textAlign:"center",padding:"38px 20px",color:"rgba(255,255,255,.22)",fontSize:".85em"}}>Ask anything, upload a file, or use your mic 🎤</div>}
          {chatHist.map((m,i)=>(
            <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
              <div style={{maxWidth:"80%",background:m.role==="user"?"linear-gradient(135deg,rgba(255,60,95,.2),rgba(155,29,255,.14))":"rgba(255,255,255,.04)",border:`1px solid ${m.role==="user"?"rgba(255,60,95,.2)":"rgba(255,255,255,.07)"}`,borderRadius:m.role==="user"?"15px 15px 4px 15px":"4px 15px 15px 15px",padding:"11px 15px",fontSize:".85em",lineHeight:1.75,color:"var(--txt)"}}>
                {m.role==="assistant"&&<div style={{fontSize:".61em",letterSpacing:"1.5px",color:"var(--R)",marginBottom:4,fontWeight:700}}>TUTOR</div>}
                <div dangerouslySetInnerHTML={{__html:md(m.content)}}/>
              </div>
            </div>
          ))}
          {chatLoad&&<div style={{display:"flex"}}><div className="nx-loader" style={{padding:"11px 16px"}}><div className="nx-x" style={{fontSize:"1.3em"}}>X</div></div></div>}
          <div ref={chatEnd}/>
        </div>

        {/* Input with voice */}
        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
          <textarea value={chatIn} onChange={e=>setCIn(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendChat()}}}
            placeholder="Ask a question… or 🎤 click mic to speak" style={{flex:1,minHeight:56}}/>
          <div style={{display:"flex",flexDirection:"column",gap:5,alignSelf:"flex-end"}}>
            <MicButton listening={listening} onToggle={toggleVoice}/>
            <button className="btn" onClick={sendChat} disabled={chatLoad} style={{height:40,width:40,padding:0,fontSize:".9em"}}>{chatLoad?"...":"↑"}</button>
          </div>
        </div>
      </div>}

      {/* QUIZ */}
      {tab==="quiz"&&<div className="fade" style={{maxWidth:640}}>
        <div className="card" style={{marginBottom:12}}>
          <div style={{display:"flex",gap:7,flexWrap:"wrap",alignItems:"center"}}>
            <input type="text" value={qTopic} onChange={e=>setQT(e.target.value)} placeholder="Quiz topic…" style={{flex:1}}/>
            <select value={qDiff} onChange={e=>setQD(e.target.value)} style={{width:130}}>
              <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option><option value="expert">Expert</option>
            </select>
            <button className="btn" onClick={nextQ} disabled={qLoad} style={{minWidth:106}}>{qLoad?"Loading…":qData?"Next →":"Start Quiz"}</button>
            <RateBadge action="quiz"/>
          </div>
        </div>
        {score.t>0&&<div style={{display:"flex",gap:11,marginBottom:12}}>
          {[{lb:"Correct",v:score.c,col:"var(--G)"},{lb:"Wrong",v:score.t-score.c,col:"var(--R)"},{lb:"Accuracy",v:Math.round(score.c/score.t*100)+"%",col:"var(--C)"}].map(s=>(
            <div key={s.lb} className="card" style={{flex:1,textAlign:"center",padding:"12px 8px"}}>
              <div style={{fontSize:"1.6em",fontWeight:800,color:s.col,fontFamily:"'Syne',sans-serif"}}>{s.v}</div>
              <div className="quiz-stat-label" style={{fontSize:".63em",color:"rgba(255,255,255,.28)",letterSpacing:"1px"}}>{s.lb.toUpperCase()}</div>
            </div>
          ))}
        </div>}
        {qData&&<div className="card fade">
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
            <span className="tag">{qDiff.toUpperCase()}</span>
            <span style={{fontSize:".7em",color:"rgba(255,255,255,.26)"}}>{qTopic}</span>
          </div>
          <div className="quiz-q-text" style={{fontSize:".95em",fontWeight:600,lineHeight:1.62,color:"var(--txt)",marginBottom:16}}>{qData.question}</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {qData.options.map((opt,idx)=>{
              let bg="rgba(255,255,255,.04)",bd="rgba(255,255,255,.09)",cl="var(--txt)";
              if(sel!==null){if(idx===qData.correct){bg="rgba(168,255,120,.1)";bd="rgba(168,255,120,.5)";cl="#a8ff78";}
                else if(idx===sel&&sel!==qData.correct){bg="rgba(255,60,95,.1)";bd="rgba(255,60,95,.5)";cl="var(--R)";}}
              return <button key={idx} onClick={()=>answer(idx)} disabled={sel!==null}
                style={{background:bg,border:`1px solid ${bd}`,color:cl,borderRadius:9,padding:"11px 15px",cursor:sel!==null?"default":"pointer",textAlign:"left",fontSize:".85em",lineHeight:1.5,fontFamily:"'Syne',sans-serif",transition:"all .18s"}}>{opt}</button>;
            })}
          </div>
          {qExp&&<div style={{marginTop:14,padding:"12px 15px",background:"rgba(0,229,255,.05)",border:"1px solid rgba(0,229,255,.18)",borderRadius:10,fontSize:".82em",color:"var(--txt2)",lineHeight:1.7}}>
            <div style={{fontWeight:700,color:"var(--C)",marginBottom:5,fontSize:".68em",letterSpacing:"1px"}}>EXPLANATION</div>{qExp}
          </div>}
          {sel!==null&&<button className="btn" onClick={nextQ} style={{marginTop:12}}>Next Question →</button>}
        </div>}
      </div>}

      {/* MOCK INTERVIEW */}
      {tab==="interview"&&<MockInterview/>}

      {/* RESUME BUILDER — visual template grid */}
      {tab==="resume"&&<div className="fade">
        <div className="tabs" style={{marginBottom:16}}>
          <button className={`tab ${rTab==="templates"?"on":""}`} onClick={()=>setRT("templates")}>Templates</button>
          <button className={`tab ${rTab==="form"?"on":""}`} onClick={()=>setRT("form")}>Your Info</button>
          <button className={`tab ${rTab==="preview"?"on":""}`} onClick={()=>rHTML&&setRT("preview")} style={{opacity:rHTML?1:.4}}>Preview</button>
        </div>

        {rTab==="templates"&&<div className="fade">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
            <div style={{fontSize:".7em",color:"rgba(255,255,255,.28)",letterSpacing:"1px"}}>{RESUME_TEMPLATES.length} ATS-OPTIMIZED TEMPLATES — click any to select</div>
            <div style={{display:"flex",gap:6}}>
              {["all","classic","modern","bold","minimal"].map(f=>(
                <button key={f} className="btnG" style={{padding:"4px 10px",fontSize:".71em"}}
                  onClick={()=>{/* filter — future enhancement */}}>{f}</button>
              ))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(148px,1fr))",gap:14}}>
            {RESUME_TEMPLATES.map(t=>(
              <TemplatePreview key={t.id} tpl={t} selected={rTpl.id===t.id}
                onClick={()=>{setRTpl(t);setRT("form");}}/>
            ))}
          </div>
        </div>}

        {rTab==="form"&&<div className="fade">
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:16}}>
            <div style={{width:16,height:16,borderRadius:4,background:"#"+rTpl.accent}}/>
            <span style={{fontWeight:700,color:"#fff",fontSize:".9em"}}>{rTpl.name}</span>
            <button className="btnG" onClick={()=>setRT("templates")} style={{fontSize:".7em",padding:"4px 9px",marginLeft:6}}>🎨 Change</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            {[{k:"name",lb:"Full Name *",ph:"Jane Doe",col:"1/-1"},
              {k:"contact",lb:"Contact *",ph:"jane@email.com | linkedin.com/in/jane | +1 555 0100 | New York",col:"1/-1"},
              {k:"summary",lb:"Professional Summary",ph:"Results-driven engineer with 5+ years...",r:3,col:"1/-1"},
              {k:"experience",lb:"Experience (role on new line, bullets with -)",ph:"Senior Engineer @ Google, 2021–Present\n- Led migration reducing latency 40%\n\nEngineer @ Stripe, 2019–2021\n- Built payment SDK used by 10k+ developers",r:7,col:"1/-1"},
              {k:"education",lb:"Education",ph:"B.S. Computer Science, MIT, 2019",r:2},
              {k:"skills",lb:"Skills",ph:"Python, React, AWS, Docker, SQL, TensorFlow",r:2},
              {k:"extra",lb:"Certifications / Awards",ph:"AWS Certified · HackMIT Winner · Google Scholar",r:2,col:"1/-1"}
            ].map(f=>(
              <div key={f.k} style={{gridColumn:f.col||"unset"}}>
                <label style={{fontSize:".66em",color:"var(--txt2)",display:"block",marginBottom:4,letterSpacing:"1.2px"}}>{f.lb.toUpperCase()}</label>
                {f.r?<textarea value={rForm[f.k]} onChange={e=>setRF(p=>({...p,[f.k]:e.target.value}))} placeholder={f.ph} style={{minHeight:f.r*22+20}}/>
                  :<input type="text" value={rForm[f.k]} onChange={e=>setRF(p=>({...p,[f.k]:e.target.value}))} placeholder={f.ph}/>}
              </div>
            ))}
          </div>
          <button className="btn" onClick={genResume} disabled={rLoad||!rForm.name} style={{marginTop:14,padding:"11px 28px"}}>
            {rLoad?"✦ AI Enhancing…":"✦ Generate ATS Resume"}
          </button>
        </div>}

        {rTab==="preview"&&rHTML&&<div className="fade">
          <div style={{display:"flex",gap:8,marginBottom:11,flexWrap:"wrap"}}>
            <button className="btn" onClick={()=>{const w=window.open("","_blank");w.document.write(rHTML);w.document.close();setTimeout(()=>w.print(),500);}}>⬇ Export PDF</button>
            <button className="btnG" onClick={()=>{const w=window.open("","_blank");w.document.write(rHTML);w.document.close();}}>⬡ Full Screen</button>
            <button className="btnG" onClick={()=>setRT("form")}>✎ Edit</button>
            <button className="btnG" onClick={()=>setRT("templates")}>🎨 Change Template</button>
          </div>
          <ResumeFrame html={rHTML}/>
        </div>}
      </div>}
    </div>
  );
}

function ResumeFrame({html}) {
  const [url,setUrl]=useState("");
  useEffect(()=>{if(!html){setUrl("");return;}const b=new Blob([html],{type:"text/html"});const u=URL.createObjectURL(b);setUrl(u);return()=>URL.revokeObjectURL(u);},[html]);
  return(
    <div style={{borderRadius:13,overflow:"hidden",border:"1px solid var(--bdr)"}}>
      <div style={{background:"var(--card-bg,rgba(26,26,42,1))",borderBottom:"1px solid var(--bdr)",padding:"8px 14px",display:"flex",gap:5}}>
        {["#ff5f57","#ffbd2e","#28ca41"].map(c=><div key={c} style={{width:10,height:10,borderRadius:"50%",background:c}}/>)}
      </div>
      {url?<iframe src={url} sandbox="allow-scripts allow-same-origin" style={{width:"100%",height:720,border:"none",display:"block"}} title="resume"/>
        :<div style={{height:720,background:"#0a0a1a",display:"flex",alignItems:"center",justifyContent:"center"}}><div className="nx-loader"><div className="nx-x">X</div><div className="nx-label">PROCESSING</div></div></div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MOCK INTERVIEW — voice I/O + scored feedback
══════════════════════════════════════════════════════════ */
function MockInterview() {
  const [role,setRole]=useState(""); const [level,setLevel]=useState("mid"); const [domain,setDomain]=useState("software engineering");
  const [started,setStarted]=useState(false); const [messages,setMsgs]=useState([]);
  const [userInput,setUI]=useState(""); const [loading,setLoad]=useState(false);
  const [speaking,setSpeaking]=useState(false); const [feedback,setFB]=useState(null); const [qCount,setQC]=useState(0);
  const endRef=useRef();
  useEffect(()=>endRef.current?.scrollIntoView({behavior:"smooth"}),[messages]);

  const {listening,toggle:toggleListen}=useSpeechInput(t=>setUI(p=>p+(p?" ":"")+t));

  function speak(text) {
    if(!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean=text.replace(/\*\*(.+?)\*\*/g,"$1").replace(/[*#`]/g,"");
    const utt=new SpeechSynthesisUtterance(clean); utt.rate=0.92; utt.pitch=1.0;
    const voices=window.speechSynthesis.getVoices();
    const v=voices.find(v=>v.name.includes("Google UK English Female"))||voices.find(v=>v.lang==="en-US")||voices[0];
    if(v) utt.voice=v; utt.onstart=()=>setSpeaking(true); utt.onend=()=>setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }
  function stopSpeak(){window.speechSynthesis?.cancel();setSpeaking(false);}

  async function startInterview() {
    if(!role.trim()) return;
    setStarted(true); setMsgs([]); setFB(null); setQC(0); setUI(""); setLoad(true);
    try {
      const intro=await callAI([{role:"user",content:`Start mock interview. Role: "${role}", Level: ${level}, Domain: ${domain}. Introduce yourself as Nexus the AI interviewer, explain the format briefly, then ask question 1.`}],
        `You are an expert interviewer at a top company. You are conducting a ${level}-level ${domain} interview for "${role}". Ask one focused question at a time. After each answer: briefly acknowledge, give 1-2 sentence feedback, then ask the next question. After 6 questions, end with "Let's wrap up" and provide: **Overall Score: X/10**, **Strengths**, **Areas to Improve**, **Final Advice**.`,
        2048,"interview");
      setMsgs([{role:"assistant",content:intro,ts:Date.now()}]); speak(intro); setQC(1);
    } finally{setLoad(false);}
  }

  async function sendAnswer() {
    if(!userInput.trim()||loading) return; stopSpeak();
    const um={role:"user",content:userInput,ts:Date.now()}; const hist=[...messages,um];
    setMsgs(hist); setUI(""); setLoad(true);
    try {
      const isEnd=hist.filter(m=>m.role==="user").length>=6;
      const reply=await callAI(hist.map(m=>({role:m.role,content:m.content})),
        `Expert interviewer for "${role}" (${level}, ${domain}).${isEnd?" This is the final answer. Wrap up the interview now. Give: **Overall Score: X/10**, **Strengths:**, **Areas to Improve:**, **Final Advice:** with 2-3 sentences each.":""}`,
        2048,"interview");
      setMsgs([...hist,{role:"assistant",content:reply,ts:Date.now()}]); speak(reply); setQC(q=>q+1);
      if(reply.includes("Overall Score")||reply.includes("wrap up")) {
        const scoreMatch=reply.match(/Overall Score[:\s]*(\d+)\s*\/\s*10/i);
        setFB({score:scoreMatch?scoreMatch[1]:null});
      }
    } finally{setLoad(false);}
  }

  if(!started) return(
    <div className="fade" style={{maxWidth:560}}>
      <div className="card" style={{marginBottom:14}}>
        <div style={{fontSize:".7em",color:"var(--txt2)",letterSpacing:"1.5px",marginBottom:14}}>INTERVIEW SETUP</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <label style={{fontSize:".68em",color:"var(--txt2)",display:"block",marginBottom:5,letterSpacing:"1.2px"}}>JOB ROLE *</label>
            <input type="text" value={role} onChange={e=>setRole(e.target.value)} placeholder="e.g. Senior Frontend Engineer, Product Manager, Data Scientist"/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            <div>
              <label style={{fontSize:".68em",color:"var(--txt2)",display:"block",marginBottom:5,letterSpacing:"1.2px"}}>LEVEL</label>
              <select value={level} onChange={e=>setLevel(e.target.value)}>
                <option value="junior">Junior (0–2 yrs)</option><option value="mid">Mid (2–5 yrs)</option>
                <option value="senior">Senior (5+ yrs)</option><option value="staff">Staff / Lead</option>
              </select>
            </div>
            <div>
              <label style={{fontSize:".68em",color:"var(--txt2)",display:"block",marginBottom:5,letterSpacing:"1.2px"}}>DOMAIN</label>
              <select value={domain} onChange={e=>setDomain(e.target.value)}>
                <option value="software engineering">Software Engineering</option><option value="product management">Product Management</option>
                <option value="data science">Data Science</option><option value="machine learning">Machine Learning</option>
                <option value="design">UX / Design</option><option value="marketing">Marketing</option>
                <option value="finance">Finance</option><option value="general">General / HR</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="card" style={{marginBottom:14,background:"rgba(0,229,255,.04)",borderColor:"rgba(0,229,255,.18)"}}>
        <div style={{fontSize:".78em",color:"var(--C)",fontWeight:700,marginBottom:7}}>🎤 How it works</div>
        <div style={{fontSize:".78em",color:"var(--txt2)",lineHeight:1.8}}>
          • 6 questions tailored to your role &amp; level<br/>
          • 🎤 Mic button — speak your answers (Chrome)<br/>
          • AI reads every question aloud — hit 🔊 to replay<br/>
          • Final score out of 10 with detailed feedback
        </div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <RateBadge action="interview"/>
        <button className="btn" onClick={startInterview} disabled={!role.trim()} style={{padding:"12px 28px",fontSize:".95em"}}>🎤 Start Interview →</button>
      </div>
    </div>
  );

  return(
    <div className="fade" style={{maxWidth:720}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div>
          <div className="interview-role-label" style={{fontWeight:700,color:"var(--txt)",fontSize:".95em"}}>{role}</div>
          <div style={{fontSize:".72em",color:"var(--txt2)"}}>{level} · {domain} · Question {Math.min(qCount,6)}/6</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {speaking&&<div style={{display:"flex",alignItems:"center",gap:5,fontSize:".74em",color:"var(--C)"}}><div className="rec-dot" style={{background:"var(--C)"}}/> Speaking</div>}
          {listening&&<div style={{display:"flex",alignItems:"center",gap:5,fontSize:".74em",color:"var(--R)"}}><div className="rec-dot"/> Listening</div>}
          <button className="btnG" onClick={()=>{stopSpeak();setStarted(false);setMsgs([]);}} style={{fontSize:".74em",padding:"5px 11px"}}>✕ End</button>
          <RateBadge action="interview"/>
        </div>
      </div>
      {/* Progress bar */}
      <div style={{height:3,background:"var(--bdr)",borderRadius:3,marginBottom:14}}>
        <div style={{height:"100%",width:`${Math.min((qCount/6)*100,100)}%`,background:"linear-gradient(90deg,var(--R),#9b1dff)",borderRadius:3,transition:"width .5s"}}/>
      </div>
      {/* Messages */}
      <div style={{display:"flex",flexDirection:"column",gap:12,maxHeight:"44vh",overflowY:"auto",marginBottom:12,paddingRight:4}}>
        {messages.map((m,i)=>(
          <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start",gap:9,alignItems:"flex-start"}}>
            {m.role==="assistant"&&<div style={{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg,var(--R),#9b1dff)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:".7em",fontWeight:700,color:"#fff",flexShrink:0,marginTop:2}}>AI</div>}
            <div className={m.role==="user"?"interview-user-bubble":"interview-assistant-bubble"} style={{maxWidth:"76%",background:m.role==="user"?"linear-gradient(135deg,rgba(255,60,95,.2),rgba(155,29,255,.14))":"rgba(255,255,255,.05)",border:`1px solid ${m.role==="user"?"rgba(255,60,95,.22)":"rgba(255,255,255,.08)"}`,borderRadius:m.role==="user"?"15px 15px 4px 15px":"4px 15px 15px 15px",padding:"12px 16px",fontSize:".86em",lineHeight:1.75,color:"var(--txt)"}}>
              {m.role==="assistant"&&<div style={{fontSize:".62em",letterSpacing:"1.5px",color:"var(--R)",marginBottom:5,fontWeight:700}}>INTERVIEWER</div>}
              <div dangerouslySetInnerHTML={{__html:md(m.content)}}/>
              {m.role==="assistant"&&<button onClick={()=>speak(m.content)} style={{marginTop:8,background:"none",border:"none",color:"var(--txt2)",cursor:"pointer",fontSize:".72em",fontFamily:"'Syne',sans-serif",padding:0}}>🔊 Replay</button>}
            </div>
          </div>
        ))}
        {loading&&<div style={{display:"flex"}}><div className="nx-loader" style={{padding:"11px 16px"}}><div className="nx-x" style={{fontSize:"1.3em"}}>X</div></div></div>}
        <div ref={endRef}/>
      </div>
      {/* Final score card */}
      {feedback&&<div className="card fade" style={{marginBottom:12,background:"rgba(168,255,120,.04)",borderColor:"rgba(168,255,120,.22)"}}>
        <div style={{fontSize:".75em",color:"var(--G)",fontWeight:700,marginBottom:8,letterSpacing:"1px"}}>✅ INTERVIEW COMPLETE</div>
        {feedback.score&&<div style={{fontSize:"2.2em",fontWeight:800,color:"var(--G)",marginBottom:10,fontFamily:"'Syne',sans-serif"}}>{feedback.score}<span style={{fontSize:".45em",color:"var(--txt2)"}}>/10</span></div>}
        <button className="btnG" onClick={()=>{stopSpeak();setStarted(false);setMsgs([]);setFB(null);}} style={{fontSize:".78em"}}>↩ Start New Interview</button>
      </div>}
      {/* Answer input */}
      {!feedback&&<div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
        <textarea value={userInput} onChange={e=>setUI(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendAnswer()}}}
          placeholder="Type your answer or 🎤 click mic to speak… (Enter to send)" style={{flex:1,minHeight:72}}/>
        <div style={{display:"flex",flexDirection:"column",gap:6,alignSelf:"flex-end"}}>
          <MicButton listening={listening} onToggle={toggleListen}/>
          <button className="btn" onClick={sendAnswer} disabled={loading||!userInput.trim()} style={{width:44,height:44,padding:0,fontSize:".9em"}}>{loading?"...":"↑"}</button>
        </div>
      </div>}
    </div>
  );
}
